import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Phone, CheckCircle2, XCircle, RotateCcw, Monitor, Users, TrendingUp, Radio, Keyboard, AlarmClock, Plus, Trash2, Pencil, Clock, Activity, Undo2, AlertTriangle, AlertCircle, PauseCircle, PlayCircle, Check, X, Lock, Unlock, Building2, ExternalLink, Ticket as TicketIcon, ChevronDown, Tv, Megaphone, Image as ImageIcon, ToggleRight, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { useMerchantChime } from "../hooks/useMerchantChime";
import { useTicketCode } from "../hooks/useTicketCode";
import QueueSettingsPanel from "../components/QueueSettingsPanel";
import CommerceLogoDialog from "../components/CommerceLogoDialog";
import TVCodeBlock from "../components/TVCodeBlock";
import QueueLimitControl from "../components/QueueLimitControl";
import TicketsHistoryPanel from "../components/TicketsHistoryPanel";
import { formatTicketCode } from "../lib/ticketCode";
import ConfirmDialog from "../components/ConfirmDialog";
import PromptDialog from "../components/PromptDialog";
import SubscriptionLockScreen from "../components/SubscriptionLockScreen";
import PaymentMethodsBadge from "../components/PaymentMethodsBadge";
import GlobalMessagePanel from "../components/GlobalMessagePanel";
import GlobalMessageDialog from "../components/GlobalMessageDialog";
import EstablishmentIconPanel from "../components/EstablishmentIconPanel";
import EstablishmentInfoTooltip from "../components/EstablishmentInfoTooltip";
import PasswordConfirmDialog from "../components/PasswordConfirmDialog";
import { useAuth } from "../contexts/AuthContext";
import BypassTicketIcon from "../components/BypassTicketIcon";
import { getCommerceIcon } from "../lib/commerceIcons";

export default function MerchantDashboard() {
  const [queue, setQueue] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [queues, setQueues] = useState([]);
  const [activeQueueId, setActiveQueueId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  // Global "end of free period" — fetched once on mount and refreshed
  // alongside the dashboard polling. Falls back to the spec default
  // (31/12/2026 23:59:59 UTC) when the public endpoint is unreachable.
  const [freePeriodEnd, setFreePeriodEnd] = useState(() => new Date("2026-12-31T23:59:59Z"));
  const [flash, setFlash] = useState(null); // 'call' | 'present' | 'absent' | null
  const [leavingIds, setLeavingIds] = useState(new Set());
  const [now, setNow] = useState(() => Date.now());
  const [showAddQueue, setShowAddQueue] = useState(false);
  const [newQueueName, setNewQueueName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name } | null
  const [closeConfirm, setCloseConfirm] = useState(false); // custom "fermer la file" modal
  const [resetConfirm, setResetConfirm] = useState(false); // custom "réinitialiser la file" modal
  // Additional-queue paywall : when the merchant tries to create a 2nd+
  // queue, a confirmation modal explains the 4,90 € TTC one-shot fee
  // before the actual creation. Holds { feeEur, pendingName } | null.
  const [feeConfirm, setFeeConfirm] = useState(null);
  const [creatingPaidQueue, setCreatingPaidQueue] = useState(false);
  // Additional-queue renewal (fork 19) : when the 24h validity of a paid
  // additional queue expires, the merchant can renew it for another 24h
  // for 4,90 € TTC. Holds the queue document being renewed | null.
  const [renewConfirm, setRenewConfirm] = useState(null);
  const [renewingQueue, setRenewingQueue] = useState(false);
  // First ponctuel queue (7,90 € TTC, 24 h — fork 2026-02) : alternative
  // au mode abonnement. Le modal sert à la fois pour l'activation initiale
  // (1ʳᵉ file ponctuelle jamais payée) ET pour la reprise après fermeture
  // ("Reprendre une nouvelle file"). Holds { mode: "activate"|"reprendre",
  // suggestedName?: string } | null.
  const [ponctuelModal, setPonctuelModal] = useState(null);
  const [ponctuelName, setPonctuelName] = useState("");
  const [activatingPonctuel, setActivatingPonctuel] = useState(false);
  // Live "now" tick used to render countdown timers on additional queues.
  // Bumped every 30s — sufficient resolution given the 24h window.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [activeTicketsWarn, setActiveTicketsWarn] = useState(null); // { id, name, count } | null
  const [renameTarget, setRenameTarget] = useState(null); // { id, currentName } | null
  const [createFirstOpen, setCreateFirstOpen] = useState(false); // first-queue name modal
  const [createFirstBusy, setCreateFirstBusy] = useState(false);
  // Password-protected destructive actions
  const [pwdDialog, setPwdDialog] = useState(null); // { kind: 'delete'|'close', target?, label, description, destructive }
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const calledIdRef = useRef(null);
  const nav = useNavigate();
  const { play } = useMerchantChime();

  const loadQueues = async () => {
    try {
      const { data } = await api.get("/merchant/queues");
      setQueues(data);
      if (!activeQueueId && data.length) setActiveQueueId(data[0].id);
      return data;
    } catch { return []; }
  };

  const load = async (qid = activeQueueId) => {
    try {
      const params = qid ? `?queueId=${qid}` : "";
      const [meRes, qRes, qsRes, ovRes] = await Promise.all([
        api.get("/merchant/me"),
        api.get(`/merchant/queue${params}`),
        api.get("/merchant/queues"),
        api.get("/merchant/overview").catch(() => ({ data: null })),
      ]);
      setCommerce(meRes.data);
      setQueue(qRes.data);
      setQueues(qsRes.data);
      setOverview(ovRes.data);
      if (!qid && qsRes.data.length) setActiveQueueId(qsRes.data[0].id);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) nav("/commerce/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(activeQueueId);
    const t = setInterval(() => load(activeQueueId), 3000);
    return () => clearInterval(t);
  }, [activeQueueId]); // eslint-disable-line

  // Live "called since" timer
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // 30s tick for additional-queue countdown badges (fork 19). Cheap and
  // gives a clean "Xh Ymin" UX without flooding renders.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Fetch the admin-configurable end-of-free-period date on mount and
  // refresh every 5 minutes. Lets the dashboard banner reflect a fresh
  // admin override without a page reload (fork 19).
  useEffect(() => {
    let alive = true;
    const fetchFP = () => {
      api.get("/system/free-period")
        .then((r) => { if (alive && r.data?.endDate) setFreePeriodEnd(new Date(r.data.endDate)); })
        .catch(() => {});
    };
    fetchFP();
    const t = setInterval(fetchFP, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Stripe return polling : when the merchant comes back from Checkout
  // with `?session_id=cs_test_...` in the URL, poll the status endpoint
  // every 2s (up to 30s) until we see `payment_status === "paid"`, then
  // refresh the dashboard. The status endpoint handles all the side
  // effects (subscription extension, queue activation, queue renewal).
  // Spec fork 19+. Polling stays in this effect to keep the dashboard
  // self-contained.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    let cancelled = false;
    const stripParam = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("session_id");
      window.history.replaceState({}, "", u.toString());
    };
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Paiement confirmé !");
          stripParam();
          await loadQueues();
          load(activeQueueId);
          // Refresh /merchant/me so the subscription banner updates.
          try {
            const r = await api.get("/merchant/me");
            setCommerce(r.data);
          } catch { /* ignore */ }
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          toast.error("Session de paiement expirée");
          stripParam();
          return;
        }
      } catch (e) {
        // Silent — keep polling, the session may not have been created yet.
      }
      if (attempts < 15) {
        setTimeout(poll, 2000);
      } else {
        toast("Paiement en attente — actualisez si nécessaire", { description: "Le webhook Stripe peut prendre quelques secondes." });
        stripParam();
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Cancel-from-Stripe cleanup (fork 24) — quand le merchant clique sur
  // « Annuler » dans le checkout Stripe, on revient avec `?cancel_session_id=…`.
  // L'endpoint backend supprime la file pré-créée en `paymentPending=True`
  // (additional / first ponctuel) et marque la transaction "canceled".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const csid = params.get("cancel_session_id");
    if (!csid) return;
    const strip = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("cancel_session_id");
      window.history.replaceState({}, "", u.toString());
    };
    (async () => {
      try {
        await api.post(`/payments/cancel/${csid}`);
        toast.info("Paiement annulé. Aucune file n'a été créée.");
      } catch {
        /* idempotent — la file n'existe peut-être déjà plus */
      }
      strip();
      try {
        await loadQueues();
        load(activeQueueId);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  // Fire quick flash when the current called ticket changes
  useEffect(() => {
    const cur = queue?.current?.id || null;
    if (cur && cur !== calledIdRef.current) {
      calledIdRef.current = cur;
      triggerFlash("call");
    }
    if (!cur) calledIdRef.current = null;
  }, [queue?.current?.id]);

  const triggerFlash = (kind) => {
    setFlash(kind);
    setTimeout(() => setFlash(null), 900);
  };

  const toggleOpen = async () => {
    if (!queue?.queue) return toast.error("Veuillez d'abord créer une file");
    // Closing the queue is destructive (resets counter, invalidates tickets) and
    // requires the establishment password per spec §3.
    if (queue.isOpen) {
      setPwdError("");
      setPwdDialog({
        kind: "close",
        target: { id: activeQueueId || queue?.queue?.id, name: queue?.queue?.name },
        label: "Fermer la file",
        description: `Confirmez la fermeture de la file « ${queue?.queue?.name || "active"} » en saisissant le mot de passe de votre compte Établissement. Tous les tickets en attente seront invalidés et le compteur réinitialisé.`,
        destructive: true,
        confirmLabel: "Fermer la file",
      });
      return;
    }
    await performToggleOpen();
  };

  const performToggleOpen = async () => {
    try {
      // Opening doesn't require a password — only closing/deleting per spec.
      const qid = activeQueueId || queue?.queue?.id;
      await api.post(`/merchant/queue/open${qid ? `?queueId=${qid}` : ""}`);
      toast.success("File ouverte");
      load(activeQueueId);
      loadQueues();
    } catch (err) {
      // Spec fork 2026-02 — si le backend exige un nouveau paiement pour
      // rouvrir une file payante expirée (4,90 € additionnelle ou 7,90 €
      // ponctuelle), on ouvre directement la bonne modale au lieu de
      // laisser le merchant face à un toast d'erreur sans action.
      const status = err.response?.status;
      const reopenKind = err.response?.headers?.["x-reopen-required"];
      if (status === 402 && reopenKind) {
        const qid = activeQueueId || queue?.queue?.id;
        const target = queues.find((qq) => qq.id === qid) || queue?.queue;
        if (reopenKind === "additional" && target) {
          setRenewConfirm(target);
          return;
        }
        if (reopenKind === "first_ponctuel" && target) {
          openPonctuelModal("reprendre", target.name);
          return;
        }
      }
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const confirmCloseQueue = async () => {
    setCloseConfirm(false);
    await performToggleOpen();
  };

  const toggleSuspendTop = async () => {
    const qid = activeQueueId || queue?.queue?.id;
    if (!qid) return;
    const isSuspended = !!queue?.queue?.isSuspended;
    try {
      const path = isSuspended ? "resume" : "suspend";
      await api.post(`/merchant/queues/${qid}/${path}`);
      toast.success(isSuspended ? "File reprise" : "File suspendue");
      load(activeQueueId);
      loadQueues();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Inline edit of the establishment display name. Only updates `commerces.name`
  // — files, tickets, QR codes, statistics and queue settings remain
  // untouched (they reference the establishment by its stable `id`).
  const startEditName = () => {
    setNameDraft(commerce?.name || "");
    setEditingName(true);
    setTimeout(() => { nameInputRef.current?.focus(); nameInputRef.current?.select(); }, 30);
  };
  const cancelEditName = () => { setEditingName(false); setNameDraft(""); };
  const saveName = async () => {
    const next = nameDraft.trim().toUpperCase();
    if (!next || next === commerce?.name) { cancelEditName(); return; }
    setSavingName(true);
    try {
      await api.patch("/merchant/me/name", { name: next });
      setCommerce((c) => (c ? { ...c, name: next } : c));
      setEditingName(false);
      setNameDraft("");
      toast.success("Nom de l'établissement mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSavingName(false);
  };

  /** QA-only (spec utilisateur 2026-02) — toggle "visible dans la recherche".
   * Réservé aux comptes `isTestAccount=True`. Permet à l'utilisateur du
   * compte TEST1 de masquer son établissement de `/api/commerces/search`
   * pendant ses essais sans polluer les résultats des visiteurs réels.
   * Le backend refuse en 403 pour tout autre compte. */
  const [togglingHidden, setTogglingHidden] = useState(false);
  const toggleHiddenFromSearch = async () => {
    if (!commerce?.isTestAccount || togglingHidden) return;
    const next = !commerce.hiddenFromSearch;
    setTogglingHidden(true);
    try {
      await api.patch("/merchant/me/hidden-from-search", { hiddenFromSearch: next });
      setCommerce((c) => (c ? { ...c, hiddenFromSearch: next } : c));
      toast.success(
        next
          ? "Établissement masqué de la recherche publique."
          : "Établissement à nouveau visible dans la recherche publique."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setTogglingHidden(false);
  };

  const callNext = async () => {
    // Spec utilisateur fork 26 — autoriser l'appel quand la file est en
    // `drainMode` (expirée mais tickets encore à traiter), même si
    // `isOpen=False`. Le merchant doit pouvoir finir d'évacuer les
    // derniers visiteurs avant que la file ne se ferme définitivement.
    if (!queue?.isOpen && !queue?.drainMode) return toast.error("La file est fermée");
    try {
      const qid = activeQueueId || queue?.queue?.id;
      const { data } = await api.post(`/merchant/queue/call-next${qid ? `?queueId=${qid}` : ""}`);
      play("primary");
      triggerFlash("call");
      toast.success(`Ticket n°${data.number} appelé`);
      setQueue((q) => q ? { ...q, current: { ...data, user: q.tickets.find(t => t.id === data.id)?.user || null }, tickets: q.tickets.map(t => t.id === data.id ? { ...t, status: "called" } : t) } : q);
      load(activeQueueId);
    } catch (err) {
      play("warn");
      toast.error(err.response?.data?.detail || "Aucun ticket en attente");
    }
  };

  const stepBack = async () => {
    try {
      const qid = activeQueueId || queue?.queue?.id;
      const { data } = await api.post(`/merchant/queue/step-back${qid ? `?queueId=${qid}` : ""}`);
      play("primary");
      triggerFlash("call");
      toast.success(`Retour en arrière : ticket n°${data.number}`);
      load(activeQueueId);
    } catch (err) {
      play("warn");
      toast.error(err.response?.data?.detail || "Impossible de revenir en arrière.");
    }
  };

  const markPresent = async (id) => {
    try {
      setLeavingIds((s) => new Set(s).add(id));
      await api.post(`/merchant/tickets/${id}/present`);
      play("success");
      triggerFlash("present");
      toast.success("Visiteur validé");
      setTimeout(() => {
        setLeavingIds((s) => { const n = new Set(s); n.delete(id); return n; });
        load();
      }, 350);
    } catch (err) {
      setLeavingIds((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const markAbsent = async (id) => {
    try {
      setLeavingIds((s) => new Set(s).add(id));
      await api.post(`/merchant/tickets/${id}/absent`);
      play("warn");
      triggerFlash("absent");
      toast.success("Visiteur marqué absent");
      setTimeout(() => {
        setLeavingIds((s) => { const n = new Set(s); n.delete(id); return n; });
        load();
      }, 350);
    } catch (err) {
      setLeavingIds((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const resetQueue = () => {
    // Open the styled confirm modal (replaces the native window.confirm).
    setResetConfirm(true);
  };

  const performResetQueue = async () => {
    setResetConfirm(false);
    try {
      const qid = activeQueueId || queue?.queue?.id;
      await api.post(`/merchant/queue/reset${qid ? `?queueId=${qid}` : ""}`);
      toast.success("File réinitialisée");
      load(activeQueueId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const createQueue = async () => {
    const name = newQueueName.trim().toUpperCase();
    if (name.length < 2) return toast.error("Nom trop court");
    try {
      // Pre-flight : check whether this queue will trigger the
      // one-shot additional-queue fee. If yes, surface a confirmation
      // modal with the price BEFORE actually creating the queue.
      const { data: quote } = await api.get("/merchant/queues/price-quote");
      if (quote?.isAdditional && quote?.feeEur > 0) {
        setFeeConfirm({ feeEur: quote.feeEur, pendingName: name });
        return;
      }
    } catch {
      // If the quote endpoint fails, fall through and let the create
      // endpoint emit its own 402 explanation.
    }
    await performCreateQueue(name, false);
  };

  const performCreateQueue = async (name, acceptAdditionalFee) => {
    try {
      // PAID PATH (fork 19+ Stripe) : if the merchant is on an active sub
      // OR on the « ponctuel » plan AND already has ≥1 queue, the new
      // queue must be paid 4,90 € TTC via Stripe Checkout. The polling
      // endpoint will materialize the queue with `paymentPending=false`
      // once Stripe confirms.
      //
      // Spec utilisateur (fork 25.17) — bug fix : sans le test `planType
      // === "ponctuel"`, un compte en mode ponctuel court-circuitait le
      // checkout Stripe et créait la file directement (visible sur
      // l'écran public mais absente de la liste merchant). On rétablit
      // le routage Stripe pour les 2 modes payants.
      const subActive = commerce?.subscriptionStatus === "active";
      const isPonctuel = commerce?.planType === "ponctuel";
      const willBeAdditional = queues.length >= 1 && (subActive || isPonctuel) && acceptAdditionalFee;
      if (willBeAdditional) {
        const { data } = await api.post("/payments/checkout/additional-queue", {
          name,
          origin_url: window.location.origin,
        });
        // Redirection to Stripe Checkout — the user comes back to
        // /merchant/dashboard?session_id=... which triggers the polling.
        window.location.href = data.url;
        return;
      }
      const { data } = await api.post("/merchant/queues", {
        name,
        ...(acceptAdditionalFee ? { acceptAdditionalFee: true } : {}),
      });
      toast.success(`File "${data.name}" créée`);
      setNewQueueName("");
      setShowAddQueue(false);
      setFeeConfirm(null);
      await loadQueues();
      setActiveQueueId(data.id);
      // Refresh the dashboard payload so the Console / Ticket sections
      // light up immediately with the freshly-created queue.
      load(data.id);
    } catch (err) {
      // 402 = subscription required (admin paid mode ON, no active sub)
      // OR additional-queue fee not accepted. The lock screen normally
      // short-circuits the dashboard but we keep a graceful toast as
      // defence-in-depth.
      if (err?.response?.status === 402) {
        toast.error(err.response.data?.detail || "Abonnement requis pour créer une file");
        // Force-refresh /merchant/me so the lock screen kicks in.
        load(activeQueueId);
        return;
      }
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const confirmPaidQueueCreation = async () => {
    if (!feeConfirm) return;
    setCreatingPaidQueue(true);
    try {
      await performCreateQueue(feeConfirm.pendingName, true);
    } finally {
      setCreatingPaidQueue(false);
    }
  };

  // Renew a paid additional queue that expired after 24h (fork 19).
  // Pops a confirmation modal first, then POSTs to the dedicated endpoint
  // with `acceptAdditionalFee=true`. On success, refresh queues.
  const performRenewAdditional = async () => {
    if (!renewConfirm) return;
    setRenewingQueue(true);
    try {
      // PAID PATH (fork 19+ Stripe) : if the merchant is on an active sub,
      // redirect to Stripe Checkout for the 4,90 € renewal fee. The
      // polling endpoint resets the 24h window once Stripe confirms.
      if (commerce?.subscriptionStatus === "active") {
        const { data } = await api.post(`/payments/checkout/queue-renewal/${renewConfirm.id}`, {
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
        return;
      }
      // Fallback : legacy ack path (used during free period, no real
      // payment because the additional-queue lifecycle is inactive then).
      await api.post(`/merchant/queues/${renewConfirm.id}/renew-additional`, {
        acceptAdditionalFee: true,
      });
      toast.success(`File "${renewConfirm.name}" renouvelée pour 24 h`);
      setRenewConfirm(null);
      await loadQueues();
      load(activeQueueId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renouvellement impossible");
    } finally {
      setRenewingQueue(false);
    }
  };

  /** Open the named-queue creation modal. The actual creation happens once
   * the user enters a name and confirms. We do NOT auto-create with a
   * default name anymore (per spec : the establishment must explicitly
   * choose what to call its first queue). */
  const createFirstQueue = () => {
    // Mode ponctuel : la 1ʳᵉ file passe obligatoirement par le checkout
    // Stripe 7,90 € TTC (24 h) — pas via le modal libre /merchant/queues.
    if (commerce?.planType === "ponctuel") {
      openPonctuelModal("activate");
      return;
    }
    setCreateFirstOpen(true);
  };

  const performCreateFirstQueue = async (raw) => {
    const name = (raw || "").trim().toUpperCase().slice(0, 20);
    if (name.length < 2) {
      toast.error("Le nom doit contenir au moins 2 caractères");
      return;
    }
    setCreateFirstBusy(true);
    try {
      const { data } = await api.post("/merchant/queues", { name });
      toast.success(`File "${data.name}" créée`);
      setCreateFirstOpen(false);
      await loadQueues();
      setActiveQueueId(data.id);
      load(data.id);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data?.detail || "Abonnement requis pour créer une file");
        setCreateFirstOpen(false);
        load(activeQueueId);
        return;
      }
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setCreateFirstBusy(false);
    }
  };

  const deleteQueue = async (qid, qname) => {
    const q = queues.find((x) => x.id === qid);
    const active = q ? (q.waitingCount || 0) + (q.calledCount || 0) : 0;
    if (active > 0) {
      setActiveTicketsWarn({ id: qid, name: qname, count: active });
      return;
    }
    // Password-protected destructive action (spec §3) — the establishment
    // must re-enter their account password before the file is permanently
    // removed.
    setPwdError("");
    setPwdDialog({
      kind: "delete",
      target: { id: qid, name: qname },
      label: "Supprimer la file",
      description: `Confirmez la suppression définitive de la file « ${qname} » en saisissant le mot de passe de votre compte Établissement.`,
      destructive: true,
      confirmLabel: "Supprimer",
    });
  };

  /** Persist the display ON/OFF switch on a queue row — auto-save. */
  const toggleQueueDisplay = async (qid, next) => {
    // Optimistic update so the switch is fully reactive.
    setQueues((cur) => cur.map((x) => (x.id === qid ? { ...x, displayOnPublic: next } : x)));
    try {
      await api.patch(`/merchant/queues/${qid}`, { displayOnPublic: next });
      toast.success(
        next
          ? "Cette file d'attente sera affichée sur l'écran public."
          : "Cette file d'attente ne sera plus affichée sur l'écran public.",
      );
    } catch (err) {
      // Revert optimistic update on failure.
      setQueues((cur) => cur.map((x) => (x.id === qid ? { ...x, displayOnPublic: !next } : x)));
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  /** Handle confirm from the password modal — routes to delete / close. */
  const performPasswordedAction = async (pwd) => {
    if (!pwdDialog) return;
    setPwdBusy(true);
    setPwdError("");
    try {
      if (pwdDialog.kind === "delete") {
        const qid = pwdDialog.target.id;
        await api.delete(`/merchant/queues/${qid}`, { headers: { "X-Confirm-Password": pwd } });
        toast.success("File supprimée");
        const remaining = queues.filter((x) => x.id !== qid);
        setQueues(remaining);
        if (activeQueueId === qid && remaining.length) setActiveQueueId(remaining[0].id);
        setPwdDialog(null);
      } else if (pwdDialog.kind === "close") {
        const qid = pwdDialog.target?.id || activeQueueId;
        await api.post(`/merchant/queue/close${qid ? `?queueId=${qid}` : ""}`, null, {
          headers: { "X-Confirm-Password": pwd },
        });
        toast.success("File fermée");
        await loadQueues();
        await load();
        setPwdDialog(null);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setPwdError("Mot de passe incorrect. Réessayez.");
      } else {
        setPwdError("");
        toast.error(err?.response?.data?.detail || "Erreur");
        setPwdDialog(null);
      }
    } finally {
      setPwdBusy(false);
    }
  };

  const performResetFromActiveWarn = async () => {
    if (!activeTicketsWarn) return;
    const { id: qid } = activeTicketsWarn;
    setActiveTicketsWarn(null);
    try {
      await api.post(`/merchant/queue/reset?queueId=${qid}`);
      toast.success("File réinitialisée — vous pouvez maintenant la supprimer");
      await loadQueues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de réinitialiser");
    }
  };

  const confirmDeleteQueue = async () => {
    if (!deleteConfirm) return;
    const { id: qid } = deleteConfirm;
    try {
      await api.delete(`/merchant/queues/${qid}`);
      toast.success("File supprimée");
      const remaining = queues.filter((q) => q.id !== qid);
      setQueues(remaining);
      if (activeQueueId === qid && remaining.length) setActiveQueueId(remaining[0].id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const renameQueue = (qid, oldName) => {
    // Open the styled prompt modal (replaces the native window.prompt).
    setRenameTarget({ id: qid, currentName: oldName });
  };

  const performRenameQueue = async (newName) => {
    if (!renameTarget) return;
    const name = (newName || "").trim().toUpperCase().slice(0, 20);
    if (!name || name === renameTarget.currentName) {
      setRenameTarget(null);
      return;
    }
    try {
      await api.patch(`/merchant/queues/${renameTarget.id}`, { name });
      toast.success("File renommée");
      loadQueues();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setRenameTarget(null);
  };

  const subscribe = async () => {
    try {
      const { data } = await api.post("/payments/checkout/subscription", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  /** Spec utilisateur Feb 2026 — anticipation : prolonger la file ponctuelle
   * 7,90 € (24 h) AVANT son expiration. Disponible dans la fenêtre des
   * 2 dernières heures. Le webhook fait un top-up : new = max(now, current) + 24 h. */
  const extendFirstPonctuel = async (queueId) => {
    if (!queueId) return;
    try {
      const { data } = await api.post(`/payments/checkout/extend-first-ponctuel/${queueId}`);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Prolongation impossible");
    }
  };

  /** Open the ponctuel-first-queue modal — either to ACTIVATE the very
   * first ponctuel queue (post free-period or after subscription expired),
   * or to REPRENDRE (re-buy) a new file after the previous one closed.
   * Both paths hit the same Stripe endpoint (7,90 € TTC, 24 h validity).
   */
  const openPonctuelModal = (mode = "activate", suggestedName = "") => {
    setPonctuelName(
      (suggestedName || "")
        .toString()
        .toUpperCase()
        .replace(/\s+/g, "")
        .slice(0, 20),
    );
    setPonctuelModal({ mode, suggestedName });
  };

  const activateFirstPonctuel = async () => {
    const name = (ponctuelName || "").trim().toUpperCase().slice(0, 20);
    if (name.length < 2) {
      toast.error("Le nom doit contenir au moins 2 caractères");
      return;
    }
    setActivatingPonctuel(true);
    try {
      const { data } = await api.post("/payments/checkout/first-queue-ponctuel", {
        name,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation impossible");
      setActivatingPonctuel(false);
    }
  };

  /** Compte de test (`isTestAccount=true`) — programme l'expiration
   * imminente d'une file (délai saisi en minutes) pour valider
   * rapidement le comportement de fin de validité (paywall pour la
   * file principale en abonnement, drainMode/closure pour les files
   * ponctuelles/additionnelles) sans attendre 24 h ou 1 mois.
   * Endpoint backend : POST /api/merchant/test/expire-soon/{queueId}
   */
  const testExpireSoon = async (qid) => {
    if (!commerce?.simulatePaymentEnabled || !qid) return;
    const raw = window.prompt(
      "Délai avant expiration (en minutes, entre 1 et 1440) :",
      "1"
    );
    if (raw === null) return; // user cancelled
    const minutes = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) {
      toast.error("Délai invalide (1 à 1440 minutes).");
      return;
    }
    try {
      const { data } = await api.post(`/merchant/test/expire-soon/${qid}`, { minutes });
      toast.success(data?.message || `Expiration programmée dans ${minutes} min.`);
      // Refresh immédiat pour visualiser le nouveau countdown.
      load(activeQueueId);
      loadQueues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };


  /** Compte de test (`isTestAccount=true`) — simule un paiement Stripe sans
   * appel réel. Reproduit côté DB le même effet que le webhook. Spec fork 24.
   * Endpoint backend : POST /api/payments/test/simulate-success
   */
  const simulatePayment = async (purpose, extra = {}) => {
    if (!commerce?.simulatePaymentEnabled) return;
    try {
      const { data } = await api.post("/payments/test/simulate-success", { purpose, ...extra });
      toast.success(`Paiement simulé (${purpose}) — rafraîchissement…`);
      // Refresh dashboard state.
      const me = await api.get("/merchant/me");
      setCommerce(me.data);
      await loadQueues();
      return data;
    } catch (err) {
      // Spec fork 25.26 — un 422 backend renvoie `detail` comme liste
      // d'objets Pydantic, qui crashe React si on les passe à `toast`.
      // On extrait un message lisible (ou un fallback).
      const detail = err?.response?.data?.detail;
      let msg = "Simulation impossible";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
      toast.error(msg);
    }
  };

  const simulatePonctuelFromModal = async () => {
    const name = (ponctuelName || "").trim().toUpperCase().slice(0, 20);
    if (name.length < 2) {
      toast.error("Le nom doit contenir au moins 2 caractères");
      return;
    }
    setActivatingPonctuel(true);
    try {
      await simulatePayment("queue_first_ponctuel", { name });
      setPonctuelModal(null);
    } finally {
      setActivatingPonctuel(false);
    }
  };

  // Keyboard shortcuts: Space = call next, P = present, A = absent
  useEffect(() => {
    const onKey = (e) => {
      if (!queue) return;
      const target = e.target;
      const isTyping = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isTyping) return;
      if (e.code === "Space") { e.preventDefault(); callNext(); return; }
      if (!queue.current) return;
      if (e.key === "p" || e.key === "P") { e.preventDefault(); markPresent(queue.current.id); }
      if (e.key === "a" || e.key === "A") { e.preventDefault(); markAbsent(queue.current.id); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [queue]); // eslint-disable-line

  const calledSince = useMemo(() => {
    if (!queue?.current?.calledAt) return null;
    const s = Math.max(0, Math.floor((now - new Date(queue.current.calledAt).getTime()) / 1000));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return { seconds: s, label: `${mm}:${ss}` };
  }, [queue?.current?.calledAt, now]);

  // Verification code for the current called ticket (merchant side)
  const codeState = useTicketCode(queue?.current?.id, "merchant", !!queue?.current);

  if (loading || !queue) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  // Subscription gate — when the admin enables paid mode and the establishment
  // does not hold an active subscription, block dashboard access entirely.
  // ⚠️ Bug fork 24 fix : le `ponctuelModal` est rendu DANS le main return
  // ci-dessous — quand le LockScreen prend la place via un return early,
  // cliquer sur « Activer 1ʳᵉ file - 7,90 € » ouvrait `ponctuelModal` mais
  // celui-ci n'était jamais monté. Solution : on rend AUSSI le modal ici.
  if (commerce?.subscriptionRequired) {
    return (
      <>
        <SubscriptionLockScreen
          commerce={commerce}
          onSubscribe={subscribe}
          onPonctuel={() => openPonctuelModal("activate")}
          onSimulateSubscription={() => simulatePayment("subscription")}
          onSimulatePonctuel={() => openPonctuelModal("activate")}
        />
        {ponctuelModal && (
          <div
            className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
            onClick={() => !activatingPonctuel && setPonctuelModal(null)}
            data-testid="ponctuel-modal-backdrop"
          >
            <div
              className="bg-white border-2 border-[#F18F18] max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="ponctuel-modal"
              role="alertdialog"
              aria-labelledby="ponctuel-modal-title-locked"
            >
              <div className="px-5 py-4 border-b border-border bg-[#F18F18]/10 flex items-center gap-3">
                <Plus size={22} className="text-[#F18F18] shrink-0" />
                <h3 id="ponctuel-modal-title-locked" className="font-display text-lg font-black text-ink">
                  Activer ma 1ʳᵉ file ponctuelle
                </h3>
              </div>
              <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
                <p>Activez votre première file en mode ponctuel — sans engagement et sans abonnement mensuel&nbsp;:</p>
                <div className="my-2 rounded-lg border-2 border-[#F18F18] bg-[#F18F18]/5 px-4 py-3 text-center">
                  <p className="overline text-[10px] text-[#F18F18] mb-1">Paiement ponctuel</p>
                  <p className="font-mono text-3xl font-black text-[#F18F18]">7,90 € TTC</p>
                  <p className="text-[11px] text-subtle mt-1">Valable 24 h</p>
                </div>
                <label className="block">
                  <span className="overline text-[10px] text-subtle mb-1 block">Nom de la file (20 caractères maximum, sans espace)</span>
                  <Input
                    value={ponctuelName}
                    onChange={(e) => setPonctuelName(e.target.value.toUpperCase().replace(/\s+/g, "").slice(0, 20))}
                    placeholder="ACCUEIL"
                    className="font-mono uppercase placeholder:text-subtle/50 placeholder:italic"
                    data-testid="ponctuel-modal-name-input"
                    autoFocus
                    maxLength={20}
                  />
                </label>
                <p className="text-xs text-ink leading-snug bg-[#F18F18]/10 border border-[#F18F18]/40 rounded-md px-3 py-2">
                  ⏱ <strong>Validité&nbsp;: 24 h</strong> à compter du paiement.<br />
                  Au-delà, la file termine les tickets en cours puis se ferme automatiquement. Pour reprendre, un nouveau paiement de 7,90 € TTC sera demandé.
                </p>
              </div>
              <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap items-center justify-end gap-2">
                <PaymentMethodsBadge className="w-full order-first mb-1" testId="ponctuel-modal-payment-methods" />
                <Button
                  variant="outline"
                  onClick={() => setPonctuelModal(null)}
                  disabled={activatingPonctuel}
                  className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                  data-testid="ponctuel-modal-cancel-btn"
                >
                  Annuler
                </Button>
                {commerce?.simulatePaymentEnabled && (
                  <Button
                    variant="outline"
                    onClick={simulatePonctuelFromModal}
                    disabled={activatingPonctuel || (ponctuelName || "").trim().length < 2}
                    className="rounded-md border-2 border-[#F18F18] text-[#F18F18] hover:bg-[#F18F18]/10 uppercase font-bold tracking-wider text-xs px-4"
                    data-testid="ponctuel-modal-simulate-btn"
                  >
                    🧪 Simuler
                  </Button>
                )}
                <Button
                  onClick={activateFirstPonctuel}
                  disabled={activatingPonctuel || (ponctuelName || "").trim().length < 2}
                  className="rounded-md bg-[#F18F18] hover:bg-[#d97e10] text-white uppercase font-bold tracking-wider text-xs px-6"
                  data-testid="ponctuel-modal-confirm-btn"
                >
                  {activatingPonctuel ? "Redirection vers Stripe…" : "Je confirme — payer 7,90 €"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const waiting = queue.tickets.filter((t) => t.status === "waiting");
  const called = queue.current;
  const noQueue = !queue.queue; // true when the establishment has not created any queue yet
  // Spec utilisateur fork 26 — détecte si la file PRINCIPALE est expirée
  // (abonnement / ponctuel / cutover / drainMode d'expiration / requiresPaidReopen).
  // Utilisé pour masquer le bouton « Ajouter une file » : les files
  // supplémentaires créées avant l'expiration restent visibles, mais on
  // ne peut plus en créer de nouvelles tant qu'une file principale active
  // n'est pas rétablie.
  const EXPIRED_REASONS = ["expired_24h", "subscription_expired_keep_alive", "post_free_period_2027"];
  const mainQueueExpired = (() => {
    const main = (queues || []).find((q) => q.isDefault);
    if (!main) return false;
    if (EXPIRED_REASONS.includes(main.closedReason)) return true;
    if (main.drainMode && EXPIRED_REASONS.includes(main.drainReason)) return true;
    if (main.requiresPaidReopen) return true;
    if (main.isFirstPonctuel && main.additionalExpiresAt) {
      try { if (new Date(main.additionalExpiresAt) <= new Date()) return true; } catch { /* ignore */ }
    }
    return false;
  })();
  const trialEnd = commerce?.trialEndDate ? new Date(commerce.trialEndDate) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;

  const flashBg =
    flash === "call" ? "bpg-flash-call"
    : flash === "present" ? "bpg-flash-present"
    : flash === "absent" ? "bpg-flash-absent"
    : "";

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-8 py-5 sm:py-8 lg:py-6 max-w-7xl">
      <style>{`
        @keyframes bpg-pulse-num { 0%,100% { transform:scale(1); } 50% { transform:scale(1.03); } }
        @keyframes bpg-ring { 0% { box-shadow:0 0 0 0 rgba(0,47,167,0.45); } 100% { box-shadow:0 0 0 22px rgba(0,47,167,0); } }
        @keyframes bpg-flash-call-kf { 0% { background:#E0E8FF; } 100% { background:#fff; } }
        @keyframes bpg-flash-pres-kf { 0% { background:#CFF8D9; } 100% { background:#fff; } }
        @keyframes bpg-flash-abs-kf  { 0% { background:#FDE2E2; } 100% { background:#fff; } }
        .bpg-current-fresh { animation: bpg-ring 1.1s ease-out 1; }
        .bpg-current-num   { animation: bpg-pulse-num 1.6s ease-in-out infinite; }
        .bpg-flash-call    { animation: bpg-flash-call-kf 0.9s ease-out 1; }
        .bpg-flash-present { animation: bpg-flash-pres-kf 0.9s ease-out 1; }
        .bpg-flash-absent  { animation: bpg-flash-abs-kf  0.9s ease-out 1; }
        @keyframes bpg-row-out { to { opacity:0; transform: translateX(8px); height:0; padding:0; } }
        .bpg-row-leaving { animation: bpg-row-out 0.3s ease-in forwards; overflow:hidden; }
      `}</style>

      {/* MON ÉTABLISSEMENT — header card with icon + name + edit pencil.
         "Tableau de bord établissement" overline kept above (smaller, discreet).
         Decorative bleu accent bar under the main title for premium feel. */}
      <div className="mb-4 sm:mb-6" data-testid="merchant-header">
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-bold text-subtle mb-2">
          Tableau de bord établissement
        </p>
        <h1
          className="font-display text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight text-ink"
          data-testid="merchant-title"
        >
          Mon établissement
        </h1>
        <span aria-hidden="true" className="block mt-2 mb-3 sm:mb-5 h-[3px] w-14 bg-brand rounded-full" />

        <div
          className="rounded-xl border border-border bg-white shadow-sm overflow-hidden"
          data-testid="merchant-card"
        >
          {editingName ? (
            <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
              <span
                aria-hidden="true"
                className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-brand/10 text-brand inline-flex items-center justify-center overflow-hidden"
                data-testid="merchant-icon-avatar"
              >
                {(() => {
                  const iconVal = commerce?.icon || "";
                  if (iconVal && iconVal.startsWith("data:")) {
                    return <img src={iconVal} alt="" className="w-full h-full object-contain" />;
                  }
                  const Preset = getCommerceIcon(iconVal);
                  return Preset ? <Preset size={22} /> : <Building2 size={22} />;
                })()}
              </span>
              <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                <input
                  ref={nameInputRef}
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") cancelEditName();
                  }}
                  maxLength={80}
                  className="flex-1 min-w-[140px] border-b-2 border-ink bg-transparent outline-none px-1 py-1 font-display font-bold text-lg sm:text-2xl text-ink truncate uppercase"
                  data-testid="merchant-name-input"
                />
                <button
                  onClick={saveName}
                  disabled={savingName || !nameDraft.trim() || nameDraft.trim() === commerce?.name}
                  className="inline-flex items-center justify-center gap-1.5 h-9 px-3 bg-[#0A7F2E] text-white hover:bg-[#086822] disabled:opacity-50 font-display font-bold text-xs uppercase tracking-wider rounded-md"
                  data-testid="merchant-name-save-btn"
                >
                  <Check size={14} /> {savingName ? "…" : "Valider"}
                </button>
                <button
                  onClick={cancelEditName}
                  className="inline-flex items-center justify-center gap-1.5 h-9 px-3 border border-border text-subtle hover:bg-surface font-display font-bold text-xs uppercase tracking-wider rounded-md"
                  title="Annuler"
                  data-testid="merchant-name-cancel-btn"
                >
                  <X size={14} /> Annuler
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Row 1 — icon avatar + full establishment name (horizontal, may wrap) */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                <span
                  aria-hidden="true"
                  className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-brand/10 text-brand inline-flex items-center justify-center overflow-hidden ring-1 ring-brand/15"
                  data-testid="merchant-icon-avatar"
                >
                  {(() => {
                    const iconVal = commerce?.icon || "";
                    if (iconVal && iconVal.startsWith("data:")) {
                      return <img src={iconVal} alt="" className="w-full h-full object-contain" />;
                    }
                    const Preset = getCommerceIcon(iconVal);
                    return Preset ? <Preset size={24} /> : <Building2 size={24} />;
                  })()}
                </span>
                <h2
                  className="flex-1 min-w-0 font-display font-black text-xl sm:text-2xl md:text-3xl text-ink leading-tight break-words"
                  title={commerce?.name}
                  data-testid="merchant-name-value"
                >
                  {commerce?.name}
                </h2>
                <EstablishmentInfoTooltip />
              </div>
              {/* Row 2 — 4 equal-sized action shortcuts (Icône · Message · Logo · Nom) */}
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 sm:px-4 pb-3 sm:pb-4 border-t border-border/60 pt-3"
                data-testid="merchant-shortcuts-row"
              >
                <EstablishmentIconPanel
                  commerce={commerce}
                  onChange={() => { api.get("/merchant/me").then((r) => setCommerce(r.data)).catch(() => {}); }}
                />
                <button
                  onClick={() => setMessageDialogOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 h-10 px-2 rounded-lg border border-border bg-white text-ink hover:border-brand hover:bg-brand/5 hover:text-brand transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                  title="Modifier le message d'accueil de l'Établissement"
                  data-testid="merchant-message-shortcut-btn"
                >
                  <Megaphone size={16} className="text-brand" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Message</span>
                </button>
                <button
                  onClick={() => setLogoDialogOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 h-10 px-2 rounded-lg border border-border bg-white text-ink hover:border-brand hover:bg-brand/5 hover:text-brand transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                  title="Importer le logo de l'Établissement"
                  data-testid="merchant-logo-shortcut-btn"
                >
                  <ImageIcon size={16} className="text-brand" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Logo</span>
                </button>
                <button
                  onClick={startEditName}
                  className="w-full inline-flex items-center justify-center gap-1.5 h-10 px-2 rounded-lg border border-border bg-white text-ink hover:border-brand hover:bg-brand/5 hover:text-brand transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                  title="Modifier le nom de l'établissement"
                  data-testid="merchant-name-edit-btn"
                >
                  <Pencil size={16} className="text-brand" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Nom</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <SubscriptionBanner commerce={commerce} daysLeft={daysLeft} onSubscribe={subscribe} onExtendPonctuel={extendFirstPonctuel} freePeriodEnd={freePeriodEnd} queues={queues} />

      {/* QA-only toggle (spec utilisateur 2026-02) — UNIQUEMENT pour les
         comptes flaggés `isTestAccount=True` côté backend (ex. TEST1).
         Permet de masquer/réafficher l'établissement dans la recherche
         publique (`/api/commerces/search`) sans toucher au reste de
         l'app. Invisible pour tous les autres comptes. */}
      {commerce?.isTestAccount && (
        <section
          className="mb-4 sm:mb-5 border-2 border-dashed border-[#F18F18] bg-[#F18F18]/5 rounded-lg p-3 sm:p-4 flex flex-wrap items-center gap-3"
          data-testid="test-account-search-visibility-banner"
        >
          <div className="flex-1 min-w-[200px]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#F18F18] font-bold mb-1">
              🧪 Compte de test
            </p>
            <p className="font-display font-black text-sm sm:text-base text-ink leading-tight">
              {commerce.hiddenFromSearch
                ? "Établissement masqué de la recherche publique."
                : "Établissement visible dans la recherche publique."}
            </p>
            <p className="text-[11px] text-subtle leading-snug mt-1">
              {commerce.hiddenFromSearch
                ? "Les visiteurs ne voient PAS ce compte dans les résultats de recherche. Réactivez l'affichage si vous souhaitez tester le parcours visiteur."
                : "Désactivez l'affichage pour utiliser l'application en test sans apparaître dans les résultats des visiteurs réels."}
            </p>
          </div>
          <Button
            onClick={toggleHiddenFromSearch}
            disabled={togglingHidden}
            className={`rounded-md font-bold text-xs uppercase tracking-wider px-4 sm:px-5 ${
              commerce.hiddenFromSearch
                ? "bg-[#0A7F2E] hover:bg-[#086822] text-white"
                : "bg-[#F18F18] hover:bg-[#d97e10] text-white"
            }`}
            data-testid="test-account-toggle-search-visibility-btn"
          >
            {togglingHidden
              ? "…"
              : commerce.hiddenFromSearch
              ? "Réafficher dans la recherche"
              : "Masquer de la recherche"}
          </Button>
        </section>
      )}

      {/* ADDITIONAL-QUEUE LIFECYCLE (fork 19) — banner listing every
         paid additional queue currently in drainMode (24h expired OR
         01/01/2027 cutover). The merchant can renew the 24h window for
         4,90 € TTC right from here for the `expired_24h` case.
         Pour les files isFirstPonctuel (7,90 €), on affiche un bouton
         "Reprendre une nouvelle file - 7,90 €" qui ouvre le modal
         d'activation ponctuelle (le doc de file est réutilisé côté backend). */}
      {queues.some((q) => q.drainMode) && (
        <section className="mb-4 sm:mb-5" data-testid="queue-lifecycle-banner">
          {queues.filter((q) => q.drainMode).map((q) => {
            const expired24h = q.drainReason === "expired_24h";
            const post2027 = q.drainReason === "post_free_period_2027";
            const firstPonctuel = !!q.isFirstPonctuel;
            return (
              <div
                key={q.id}
                className="border-2 border-destructive bg-destructive/5 p-3 sm:p-4 mb-2 flex flex-wrap items-center gap-3"
                data-testid={`queue-drain-banner-${q.id}`}
              >
                <span aria-hidden="true" className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-destructive/15 text-destructive shrink-0">⏱</span>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-bold text-destructive">
                    File « {q.name} »&nbsp;: fermeture programmée
                  </p>
                  <p className="text-xs text-subtle leading-snug mt-0.5">
                    {expired24h && firstPonctuel && "Durée de validité de 24 h écoulée. La file termine les tickets en cours puis se ferme. Pour reprendre une nouvelle file, un paiement de 7,90 € TTC sera demandé (24 h supplémentaires)."}
                    {expired24h && !firstPonctuel && "Durée de validité de 24 h écoulée. La file termine les tickets en cours puis se ferme. Renouvelez pour 4,90 € TTC afin de la rouvrir pour 24 h supplémentaires."}
                    {post2027 && "La période de gratuité est terminée. Souscrivez à l'abonnement pour rouvrir cette file."}
                    {!expired24h && !post2027 && "Cette file est en fermeture programmée."}
                  </p>
                </div>
                {expired24h && firstPonctuel && (
                  <Button
                    onClick={() => openPonctuelModal("reprendre", q.name)}
                    className="h-9 px-4 rounded-md bg-[#F18F18] hover:bg-[#d97e10] text-white font-bold text-xs uppercase tracking-wider"
                    data-testid={`queue-reprendre-btn-${q.id}`}
                  >
                    Reprendre — 7,90 €
                  </Button>
                )}
                {expired24h && !firstPonctuel && (
                  <Button
                    onClick={() => setRenewConfirm(q)}
                    className="h-9 px-4 rounded-md bg-brand hover:bg-brand-hover text-white font-bold text-xs uppercase tracking-wider"
                    data-testid={`queue-renew-btn-${q.id}`}
                  >
                    Renouveler 4,90 €
                  </Button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* FILES EN COURS — quick overview of the current "waiting count"
         per active queue. Per 2026-02 spec the inner card no longer
         shows the static `currentNumber` (e.g. C000) — only the queue
         name and the live waiting count remain. */}
      {queues.length > 0 && (
        <section className="mb-4 sm:mb-5" data-testid="now-serving-section" aria-label="Files en cours">
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3">
            <span aria-hidden="true" className="hidden sm:block h-px w-12 bg-border" />
            <span aria-hidden="true" className="text-brand text-sm font-black select-none">•</span>
            <h2
              className="font-display font-black uppercase tracking-[0.04em] text-base sm:text-xl text-ink text-center"
              data-testid="now-serving-title"
            >
              {queues.length > 1 ? "Files en cours" : "File en cours"}
            </h2>
            <span aria-hidden="true" className="text-brand text-sm font-black select-none">•</span>
            <span aria-hidden="true" className="hidden sm:block h-px w-12 bg-border" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
            {queues.map((q) => {
              const inactive = q.isOpen === false || q.isSuspended;
              // Spec utilisateur fork 25.25 — décompte visible sur chaque
              // carte file (tous comptes, existants + futurs) :
              //   • file additionnelle 4,90 € ou 1ʳᵉ ponctuelle 7,90 € →
              //     « Il reste Xh YYmin » à partir d'`additionalExpiresAt`.
              //   • file principale couverte par l'abonnement 9,90 €/mois
              //     → « Il reste N jour(s) » à partir de `subscriptionEndDate`.
              //   • file fermée pour expiration / sans validité → pas de chip.
              let countdownChip = null;
              const expired = ["expired_24h", "post_free_period_2027", "subscription_expired_keep_alive"].includes(q.closedReason);
              if (!expired && !q.drainMode) {
                // Case A : queue à 24 h (4,90 € ou 7,90 € ponctuelle).
                if (q.additionalExpiresAt) {
                  const remainMs = new Date(q.additionalExpiresAt).getTime() - nowTick;
                  if (remainMs > 0) {
                    const h = Math.floor(remainMs / 3600000);
                    const m = Math.floor((remainMs % 3600000) / 60000);
                    const urgent = remainMs < 3600000; // <1h
                    countdownChip = (
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${urgent ? "bg-destructive/15 text-destructive" : "bg-brand/10 text-brand"}`}
                        title={`Expire le ${new Date(q.additionalExpiresAt).toLocaleString("fr-FR")}`}
                        data-testid={`queue-expiry-chip-${q.id}`}
                      >
                        ⏱ Il reste {h}h {m.toString().padStart(2, "0")}min
                      </span>
                    );
                  }
                } else if (
                  // Case B : file principale en mode abonnement actif.
                  // On la reconnaît à : pas d'`additionalExpiresAt`, le
                  // commerce a un abonnement actif avec une date de fin.
                  !q.isAdditional &&
                  commerce?.subscriptionStatus === "active" &&
                  commerce?.subscriptionEndDate
                ) {
                  const endTs = new Date(commerce.subscriptionEndDate).getTime();
                  const remainMs = endTs - nowTick;
                  if (remainMs > 0) {
                    const days = Math.ceil(remainMs / 86_400_000);
                    const urgent = days <= 3;
                    countdownChip = (
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${urgent ? "bg-destructive/15 text-destructive" : "bg-brand/10 text-brand"}`}
                        title={`Abonnement valide jusqu'au ${new Date(commerce.subscriptionEndDate).toLocaleDateString("fr-FR")}`}
                        data-testid={`queue-subscription-chip-${q.id}`}
                      >
                        📅 Il reste {days} jour{days > 1 ? "s" : ""}
                      </span>
                    );
                  }
                }
              }
              return (
                <div
                  key={q.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveQueueId(q.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveQueueId(q.id); } }}
                  className={`cursor-pointer border-2 p-3 flex items-center gap-3 transition-colors ${inactive ? "opacity-60" : ""} ${
                    activeQueueId === q.id
                      ? "border-brand bg-brand/5 ring-2 ring-brand/30"
                      : "border-border bg-white hover:border-brand/60"
                  }`}
                  aria-pressed={activeQueueId === q.id}
                  data-testid={`now-serving-card-${q.id}`}
                >
                  <span
                    className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
                      q.isSuspended ? "bg-[#F18F18] animate-pulse"
                        : q.isOpen === false ? "bg-destructive"
                        : "bg-[#0A7F2E]"
                    }`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-subtle font-bold truncate">
                      File
                    </p>
                    <p
                      className="font-display text-base sm:text-lg font-black text-ink leading-tight mt-1 truncate"
                      data-testid={`now-serving-name-${q.id}`}
                    >
                      {q.name}
                    </p>
                    {countdownChip && <div className="mt-1">{countdownChip}</div>}
                    {commerce?.simulatePaymentEnabled && (
                      <button
                        onClick={(e) => { e.stopPropagation(); testExpireSoon(q.id); }}
                        className="mt-1 text-[10px] font-bold text-[#F18F18] underline decoration-dotted underline-offset-2 hover:text-[#d97e10] whitespace-nowrap"
                        data-testid={`queue-test-expire-soon-${q.id}`}
                        title="Compte test : saisissez le délai (en minutes) avant expiration"
                      >
                        🧪 Expirer dans…
                      </button>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {(() => {
                      // Spec utilisateur fork 25.21 — quand la file est
                      // fermée pour cause d'expiration (24 h écoulées,
                      // abonnement expiré pendant que des add. tournent,
                      // ou cutover 01/01/2027), on remplace le compteur
                      // "EN ATTENTE" par un libellé "EXPIRÉ" rouge & gras
                      // qui rend visible le statut au premier coup d'œil.
                      // Applicable à TOUS les comptes (existants + futurs).
                      const expiredReasons = [
                        "expired_24h",
                        "post_free_period_2027",
                        "subscription_expired_keep_alive",
                      ];
                      // Fork 26 — couvre aussi la phase transitoire
                      // `drainMode=True` (file fermée mais tickets encore
                      // à traiter), pour que l'établissement voie
                      // clairement qu'elle a expiré.
                      const isExpired = expiredReasons.includes(q.closedReason) ||
                        (q.drainMode && expiredReasons.includes(q.drainReason));
                      if (isExpired) {
                        return (
                          <>
                            <p className="text-[10px] uppercase tracking-wider text-subtle font-bold">
                              Statut
                            </p>
                            <p
                              className="font-display text-base sm:text-lg font-black text-destructive leading-none mt-1 uppercase tracking-wider"
                              data-testid={`queue-status-expired-${q.id}`}
                            >
                              Expiré
                            </p>
                          </>
                        );
                      }
                      return (
                        <>
                          <p className="text-[10px] uppercase tracking-wider text-subtle font-bold">
                            En attente
                          </p>
                          <p className="font-mono text-lg font-black text-ink leading-none mt-1">
                            {q.waitingCount ?? 0}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CONSOLE DE GESTION — premium control surface (4 cards).
         Each card uses tinted background + colored icon medallion + status pill +
         description, and a top-right Switch for toggle controls (open/suspend/limit). */}
      <section className="mb-4 sm:mb-5" data-testid="console-section" aria-label="Console de gestion">
        <div className="flex items-center justify-center gap-3 sm:gap-4 mb-1" data-testid="console-section-title-wrap">
          <span aria-hidden="true" className="hidden sm:block h-px w-12 bg-border" />
          <span aria-hidden="true" className="text-brand text-sm font-black select-none">•</span>
          <h2
            className="font-display font-black uppercase tracking-[0.04em] text-xl sm:text-2xl md:text-3xl text-ink text-center"
            data-testid="console-title"
          >
            Console de gestion
          </h2>
          <span aria-hidden="true" className="text-brand text-sm font-black select-none">•</span>
          <span aria-hidden="true" className="hidden sm:block h-px w-12 bg-border" />
        </div>
        <div aria-hidden="true" className="mx-auto mb-4 h-[3px] w-14 bg-brand rounded-full" />

        {/* No-queue empty state CTA — replaces the queue-related cards as
           long as no queue exists. Per spec, the first queue must be born
           from an explicit click.
           Spec utilisateur fork 26 — ce bloc ne doit apparaître que si
           AUCUNE file n'a JAMAIS été créée par l'établissement (premier
           login). Dès qu'au moins une file existe (même fermée/expirée),
           on le masque, car c'est la file existante qui doit être
           sélectionnée par défaut. */}
        {noQueue && (queues?.length || 0) === 0 && (
          <div
            className="mb-4 rounded-2xl border-2 border-dashed border-brand/40 bg-brand/5 p-5 sm:p-6 text-center"
            data-testid="no-queue-empty-state"
          >
            <span aria-hidden="true" className="inline-flex items-center justify-center w-16 h-16 mb-3">
              <BypassTicketIcon size={56} />
            </span>
            <h3 className="font-display font-black uppercase tracking-[0.06em] text-base sm:text-lg text-ink">
              Aucune file d'attente
            </h3>
            <p className="mt-1 text-sm text-subtle max-w-md mx-auto">
              Créez votre première file pour commencer à recevoir des visiteurs.
            </p>
            <button
              type="button"
              onClick={createFirstQueue}
              className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md bg-brand text-white hover:bg-brand-hover font-display font-black uppercase tracking-[0.12em] text-sm shadow-sm transition-colors"
              data-testid="create-first-queue-btn"
            >
              <Plus size={16} /> Créer ma première file
            </button>
          </div>
        )}

        {/* File selector — shows when >1 queue. When the establishment has
           no queue yet, displays a disabled placeholder reading "Aucune file". */}
        {(queues.length > 1 || noQueue) && (
          <div className="mb-3 sm:mb-4 flex items-center justify-center gap-2 flex-wrap" data-testid="console-queue-selector-wrap">
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-subtle">File sélectionnée&nbsp;:</span>
            <div className="relative inline-block">
              {noQueue ? (
                <span
                  className="inline-flex items-center pl-3 pr-9 h-9 rounded-lg border border-border bg-surface/60 text-subtle font-display font-bold text-sm italic"
                  data-testid="console-queue-selector-empty"
                >
                  Aucune file
                </span>
              ) : (
                <select
                  value={activeQueueId || ""}
                  onChange={(e) => setActiveQueueId(e.target.value)}
                  className="appearance-none pl-3 pr-9 h-9 rounded-lg border border-border bg-white text-ink font-display font-bold text-sm shadow-sm hover:border-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand cursor-pointer transition-colors"
                  data-testid="console-queue-selector"
                  aria-label="Choisir la file gérée par la console"
                >
                  {queues.map((q) => (
                    <option key={q.id} value={q.id}>{q.name}</option>
                  ))}
                </select>
              )}
              <span aria-hidden="true" className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-md bg-ink/5 text-ink pointer-events-none">
                <ChevronDown size={12} strokeWidth={3} />
              </span>
            </div>
          </div>
        )}

        {/* Spec utilisateur fork 25.27 — ligne « Programmer votre file »,
           visible uniquement quand la file PRINCIPALE est sélectionnée
           (isAdditional=false, isFirstPonctuel=false), pour tous les
           comptes en abonnement actif. La programmation est ignorée si
           la file est déjà ouverte au moment où l'heure arrive — c'est
           voulu, le merchant peut planifier à l'avance.
           Spec fork 25.28 — pour garantir que `scheduledOpenAt` persiste
           bien à l'aller-retour sur d'autres files, on cherche d'abord
           la file dans l'array `queues` (fraîchement mis à jour toutes
           les 3 s par le polling `load`) ; en fallback, on prend
           `queue.queue` (la file actuellement détaillée). */}
        {(() => {
          const fromList = queues.find((qq) => qq.id === activeQueueId);
          const selected = fromList || queue?.queue;
          if (!selected) return null;
          if (selected.isAdditional || selected.isFirstPonctuel) return null;
          if (commerce?.subscriptionStatus !== "active") return null;
          // Spec utilisateur fork 25.29 — la programmation n'est possible
          // que sur une file fermée. Si la file est ouverte, on cache
          // entièrement la ligne (pour éviter toute confusion entre
          // ouverture en cours et ouverture planifiée).
          if (selected.isOpen) return null;
          return (
            <ScheduleOpenRow
              key={selected.id}
              queue={selected}
              commerce={commerce}
              onScheduled={() => { load(activeQueueId); loadQueues(); }}
            />
          );
        })()}

        <div className={`grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 ${noQueue ? "opacity-50 pointer-events-none select-none" : ""}`} data-testid="console-controls-grid" aria-disabled={noQueue}>
          {/* 1) Écran public — informational card with TV code + open button */}
          <div
            className="relative flex flex-col items-center text-center rounded-2xl border-2 border-brand/25 bg-brand/5 p-2.5 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
            data-testid="public-display-card"
          >
            <span aria-hidden="true" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand/15 text-brand inline-flex items-center justify-center mb-2 sm:mb-3">
              <Monitor size={22} />
            </span>
            <h3 className="font-display font-black uppercase tracking-[0.06em] text-sm sm:text-base text-ink leading-tight">
              Écran public
            </h3>
            {commerce?.tvCode ? (
              <p className="mt-1.5 text-xs sm:text-sm text-subtle">
                Code TV&nbsp;: <span className="font-mono font-black text-brand text-base sm:text-lg align-middle ml-0.5">{commerce.tvCode}</span>
              </p>
            ) : (
              <p className="mt-1.5 text-[11px] text-subtle">Affichage côté visiteur</p>
            )}
            <a
              href={`/display/${commerce.id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border-2 border-brand text-brand hover:bg-brand hover:text-white font-bold text-xs uppercase tracking-wider transition-colors"
              data-testid="public-display-link"
            >
              <ExternalLink size={13} /> Ouvrir l'écran
            </a>
            <a
              href={`/display/${commerce.id}?tv=1`}
              target="_blank"
              rel="noreferrer"
              className="mt-1.5 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-ink text-white hover:bg-ink/90 font-bold text-xs uppercase tracking-wider transition-colors"
              title="Plein écran automatique + alerte sonore à chaque appel"
              data-testid="public-display-tv-mirror"
            >
              <Tv size={13} /> Mode TV Miroir
            </a>
          </div>

          {/* 2) Ouverte / Fermée */}
          <div
            className={`relative flex flex-col items-center text-center rounded-2xl border-2 p-2.5 sm:p-4 shadow-sm transition-shadow hover:shadow-md ${
              queue.isOpen
                ? "border-[#0A7F2E]/40 bg-[#0A7F2E]/5"
                : "border-destructive/40 bg-destructive/5"
            }`}
            data-testid="queue-toggle-card"
          >
            <BigSwitch
              checked={queue.isOpen}
              onChange={toggleOpen}
              activeBg="#0A7F2E"
              testid="queue-toggle-switch"
              ariaLabel={queue.isOpen ? "Fermer la file" : "Ouvrir la file"}
            />
            <span
              aria-hidden="true"
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full inline-flex items-center justify-center mb-2 sm:mb-3 ${
                queue.isOpen ? "bg-[#0A7F2E]/15 text-[#0A7F2E]" : "bg-destructive/15 text-destructive"
              }`}
            >
              {queue.isOpen ? <Unlock size={22} /> : <Lock size={22} />}
            </span>
            <h3 className="font-display font-black uppercase tracking-[0.06em] text-sm sm:text-base text-ink leading-tight" data-testid="queue-toggle">
              Ouverte / Fermée
            </h3>
            <span
              className={`mt-2 inline-block px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider ${
                queue.isOpen ? "bg-[#0A7F2E]/15 text-[#0A7F2E]" : "bg-destructive/15 text-destructive"
              }`}
            >
              {queue.isOpen ? "Ouverte" : "Fermée"}
            </span>
            <p className="mt-2 text-[11px] text-subtle leading-snug">
              {queue.isOpen ? "La file d'attente est ouverte" : "La file d'attente est fermée"}
            </p>
          </div>

          {/* 3) Suspendre */}
          <div
            className="relative flex flex-col items-center text-center rounded-2xl border-2 border-[#F18F18]/40 bg-[#F18F18]/5 p-2.5 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
            data-testid="queue-suspend-card"
          >
            <BigSwitch
              checked={!!queue?.queue?.isSuspended}
              onChange={toggleSuspendTop}
              activeBg="#F18F18"
              testid="queue-suspend-switch"
              ariaLabel={queue?.queue?.isSuspended ? "Reprendre la file" : "Suspendre la file"}
            />
            <span aria-hidden="true" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F18F18]/15 text-[#F18F18] inline-flex items-center justify-center mb-2 sm:mb-3">
              {queue?.queue?.isSuspended ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
            </span>
            <h3 className="font-display font-black uppercase tracking-[0.06em] text-sm sm:text-base text-ink leading-tight" data-testid="queue-suspend-top-btn">
              Suspendre
            </h3>
            <span className="mt-2 inline-block px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider bg-[#F18F18]/15 text-[#F18F18]">
              {queue?.queue?.isSuspended ? "Suspendue" : "En service"}
            </span>
            <p className="mt-2 text-[11px] text-subtle leading-snug">
              {queue?.queue?.isSuspended ? "Aucun nouveau ticket" : "La file fonctionne normalement"}
            </p>
          </div>

          {/* 4) Limite de file (optionnelle) — switch + opens modal when activated.
             Replaced by a placeholder card when no queue exists. */}
          {noQueue ? (
            <div
              className="relative flex flex-col items-center text-center rounded-2xl border-2 border-border bg-surface/40 p-2.5 sm:p-4 shadow-sm"
              data-testid="queue-limit-placeholder"
            >
              <span aria-hidden="true" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-ink/5 text-subtle inline-flex items-center justify-center mb-2 sm:mb-3">
                <Users size={22} />
              </span>
              <h3 className="font-display font-black uppercase tracking-[0.06em] text-sm sm:text-base text-ink leading-tight">
                Limite de file
              </h3>
              <span className="mt-2 inline-block px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider bg-ink/5 text-subtle">
                Indisponible
              </span>
              <p className="mt-2 text-[11px] text-subtle leading-snug">
                Créez une file pour configurer
              </p>
            </div>
          ) : (
            <QueueLimitControl
              queue={queues.find((q) => q.id === activeQueueId)}
              onChange={() => { loadQueues(); load(activeQueueId); }}
            />
          )}
        </div>
      </section>

      {/* FILES D'ATTENTE moved BELOW the live "Ticket en cours" grid per spec :
         the operator must see Console → Ticket → Files on a single mobile
         screen with minimal scroll. */}

      {/* TICKET EN COURS + APERÇU RAPIDE — sur lg+ (PC), affichage en
         2 colonnes côte à côte pour utiliser toute la largeur écran et
         éviter le scroll vertical. Mobile : empilement identique à avant. */}
      <div className="mb-4 sm:mb-5 max-w-2xl lg:max-w-none mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 lg:items-start">
          {/* Colonne 1 — Ticket en cours + raccourcis clavier */}
          <div>
          <div className={`rounded-2xl border-2 ${called ? "border-brand" : "border-border"} bg-white shadow-sm overflow-hidden ${flashBg}`} data-testid="current-ticket-card">
            <div className="px-3 sm:px-4 py-2.5 border-b border-border bg-surface/60 flex items-center justify-center gap-2 relative">
              <TicketIcon size={16} className="text-brand" />
              <p className="font-display font-black uppercase tracking-[0.12em] text-xs sm:text-sm text-ink">Ticket en cours</p>
              {called && calledSince && (
                <span className={`absolute right-3 inline-flex items-center gap-1 font-mono text-[10px] font-bold ${calledSince.seconds > 90 ? "text-destructive" : "text-subtle"}`} data-testid="called-since">
                  <AlarmClock size={11} /> {calledSince.label}
                </span>
              )}
            </div>
            <div className={`px-4 py-3 sm:py-4 text-center ${called ? "bpg-current-fresh" : ""}`}>
              {called ? (
                <>
                  <p
                    className={`font-mono text-5xl sm:text-6xl md:text-7xl font-black text-ink leading-none bpg-current-num`}
                    data-testid="current-number"
                  >
                    {formatTicketCode(called)}
                  </p>
                  {called.type === "priority" ? (
                    <p className="mt-2 inline-flex items-center gap-1 text-brand font-bold uppercase text-xs">
                      <Radio size={12} /> Distance
                    </p>
                  ) : null}

                  {/* VERIFICATION CODE — visual check */}
                  <div className="mt-4 border-2 border-ink rounded-md p-3" data-testid="merchant-code-block">
                    <p className="overline text-center">Code du ticket · vérification visuelle</p>
                    <div className="mt-1.5 flex items-end justify-between gap-3">
                      <p
                        className="font-mono text-3xl sm:text-4xl font-black tracking-[0.2em] text-ink leading-none"
                        data-testid="merchant-verification-code"
                      >
                        {codeState.loading ? "----" : codeState.code || "----"}
                      </p>
                      <div className="flex-1 max-w-[80px]">
                        <div className="h-1.5 bg-surface overflow-hidden border border-border rounded">
                          <div
                            className="h-full bg-brand transition-all duration-1000 linear"
                            style={{
                              width: codeState.remainingSec != null
                                ? `${(codeState.remainingSec / codeState.total) * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                        <p className="text-[9px] text-subtle text-right mt-1 font-mono">
                          {codeState.remainingSec != null ? `${codeState.remainingSec}s` : "—"}
                        </p>
                      </div>
                    </div>
                    <p className="mt-1.5 text-[10px] text-subtle leading-snug">
                      Comparez avec le code affiché sur le téléphone du visiteur.
                    </p>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button onClick={() => markPresent(called.id)} className="h-11 bg-[#0A7F2E] hover:bg-[#086822] text-white rounded-md font-bold" data-testid="mark-present-btn">
                      <CheckCircle2 size={15} className="mr-1.5" /> Valider <kbd className="ml-1 text-[10px] opacity-70">P</kbd>
                    </Button>
                    <Button onClick={() => markAbsent(called.id)} variant="outline" className="h-11 border-2 border-destructive text-destructive hover:bg-destructive hover:text-white rounded-md font-bold" data-testid="mark-absent-btn">
                      <XCircle size={15} className="mr-1.5" /> Absent <kbd className="ml-1 text-[10px] opacity-70">A</kbd>
                    </Button>
                  </div>
                </>
              ) : (
                <div className="py-2 sm:py-3 flex flex-row items-center justify-center gap-3" data-testid="ticket-empty-state">
                  <span aria-hidden="true" className="shrink-0 w-12 h-12 inline-flex items-center justify-center">
                    <BypassTicketIcon size={42} />
                  </span>
                  <div className="text-left">
                    <p className="font-bold text-ink text-sm">Aucun ticket appelé</p>
                    <p className="text-[11px] text-subtle">
                      Cliquez sur <strong className="text-ink">Appeler le suivant</strong> pour démarrer.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="px-3 sm:px-4 pb-3 pt-2 border-t border-border space-y-2">
              {/* Spec utilisateur fork 26 — quand la file est en `drainMode`
                 (expirée mais avec des tickets encore en attente), l'établissement
                 DOIT pouvoir continuer à appeler/valider/absenter les tickets
                 existants même si `isOpen=False`. On considère donc la file
                 « opérable » dès qu'elle est ouverte OU en drain. */}
              {(() => null)()}
              <Button
                onClick={callNext}
                disabled={noQueue || (!queue.isOpen && !queue.drainMode) || waiting.length === 0}
                className="w-full h-12 bg-brand hover:bg-brand-hover text-white rounded-md font-bold text-base"
                data-testid="call-next-btn"
              >
                <Phone size={17} className="mr-2" /> Appeler le suivant
                <kbd className="ml-2 text-[11px] border border-white/40 px-1.5 py-0.5 rounded bg-white/10">Espace</kbd>
              </Button>
              <Button
                onClick={stepBack}
                disabled={noQueue}
                variant="outline"
                className="w-full h-10 border-2 border-ink text-ink hover:bg-ink hover:text-white rounded-md font-bold disabled:opacity-50"
                data-testid="step-back-btn"
              >
                <Undo2 size={15} className="mr-2" /> Revenir en arrière
              </Button>
              {noQueue && (
                <p className="text-[11px] text-subtle text-center" data-testid="no-queue-hint">
                  Créez une file pour activer les actions.
                </p>
              )}
              {!noQueue && !queue.isOpen && !queue.drainMode && <p className="text-[11px] text-destructive text-center">Ouvrez la file pour pouvoir appeler.</p>}
              {!noQueue && queue.drainMode && waiting.length > 0 && (
                <p className="text-[11px] text-destructive text-center font-bold" data-testid="queue-drain-hint">
                  File expirée — traitez les derniers tickets puis la console basculera vers l'écran de choix.
                </p>
              )}
              {!noQueue && (queue.isOpen || queue.drainMode) && waiting.length === 0 && <p className="text-[11px] text-subtle text-center">Aucun visiteur en attente.</p>}
              {!noQueue && queue.isOpen && (
                <p className="text-[10px] text-subtle text-center leading-snug">La réinitialisation est automatique à chaque réouverture d'une file fermée.</p>
              )}
            </div>
          </div>

          {/* Shortcut hint */}
          {/* Spec utilisateur fork 25.35 — bloc « Espace · Appeler · P
             Présent · A Absent » supprimé du dashboard de tous les
             comptes (existants et futurs). Les raccourcis clavier
             restent fonctionnels, mais l'aide visuelle est masquée. */}
          </div>
          {/* /Colonne 1 */}

          {/* Spec utilisateur fork 25.35 — bloc « APERÇU RAPIDE ·
             AUJOURD'HUI » supprimé du dashboard de tous les comptes.
             Les métriques restent disponibles dans /merchant/analytics. */}
        </div>
      </div>

      {/* FILES D'ATTENTE block — moved below the Ticket en cours grid per spec
         (Console → Ticket → Files visible on a single mobile screen). */}
      <div className="mb-4 sm:mb-6 rounded-2xl border border-border bg-white shadow-sm overflow-hidden" data-testid="queues-tabs">
        <div className="px-3 sm:px-5 py-3 flex items-center justify-between gap-2 border-b border-border">
          <span aria-hidden="true" className="shrink-0 inline-flex items-center justify-center text-brand">
            <Users size={18} />
          </span>
          <h2
            className="flex-1 text-center font-display font-black uppercase tracking-[0.06em] text-base sm:text-lg text-ink"
            data-testid="queues-section-title"
          >
            Files d'attente
          </h2>
          <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md bg-surface border border-border text-[10px] sm:text-[11px] font-bold text-subtle" data-testid="queues-count-pill">
            10 files maximum
          </span>
        </div>

        <div className="grid grid-cols-[112px_1fr] sm:grid-cols-[180px_1fr]">
          <div className="p-2 sm:p-3 border-r border-border bg-surface/40 flex flex-col gap-1.5 sm:gap-2">
            {queues.length >= 10 || mainQueueExpired ? (
              <div />
            ) : !showAddQueue ? (
              <button
                onClick={() => setShowAddQueue(true)}
                className="inline-flex items-center justify-center gap-1 h-9 sm:h-12 rounded-md bg-brand text-white hover:bg-brand-hover px-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.06em] shadow-sm"
                data-testid="queue-add-btn"
              >
                <Plus size={12} /> Ajouter une file
              </button>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Input
                  autoFocus
                  value={newQueueName}
                  onChange={(e) => setNewQueueName(e.target.value.toUpperCase().slice(0, 20))}
                  onKeyDown={(e) => { if (e.key === "Enter") createQueue(); if (e.key === "Escape") { setShowAddQueue(false); setNewQueueName(""); } }}
                  placeholder="ACCUEIL..."
                  className="h-9 text-sm uppercase"
                  maxLength={20}
                  data-testid="queue-new-name-input"
                />
                <div className="flex gap-1">
                  <button onClick={createQueue} className="flex-1 h-8 rounded-md bg-brand text-white text-xs font-bold uppercase tracking-wider" data-testid="queue-new-save-btn">Créer</button>
                  <button onClick={() => { setShowAddQueue(false); setNewQueueName(""); }} className="flex-1 h-8 rounded-md border border-border text-subtle text-xs font-bold uppercase tracking-wider">Annuler</button>
                </div>
              </div>
            )}
          </div>

          <div className="divide-y divide-border min-w-0">
            {queues.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-subtle italic" data-testid="files-list-empty">
                Aucune file pour l'instant. Cliquez sur « Ajouter une file » pour en créer une.
              </div>
            )}
            {queues.map((q, idx) => (
              <QueueRow
                key={q.id}
                index={idx + 1}
                queue={q}
                active={activeQueueId === q.id}
                onSelect={() => setActiveQueueId(q.id)}
                onRename={() => renameQueue(q.id, q.name)}
                onDelete={() => deleteQueue(q.id, q.name)}
                onToggleDisplay={(next) => toggleQueueDisplay(q.id, next)}
              />
            ))}
          </div>
        </div>

        {queues.length >= 10 && (
          <div className="m-3 rounded-md border border-brand/30 bg-brand/5 px-3 py-2 text-xs text-ink flex items-start gap-2" data-testid="queue-limit-help">
            <AlertCircle size={14} className="text-brand mt-0.5 shrink-0" />
            <div>
              <p className="font-bold">Limite de 10 files atteinte</p>
              <p className="text-subtle">Vous êtes limité à 10 files d'attente maximum.</p>
            </div>
          </div>
        )}
      </div>

      {/* Paramètres / QR / Message personnalisé — placed AFTER the files list
         per spec : the operator first sees Console → Ticket → Files, and only
         then accesses the settings of the active queue. The dropdown lets the
         merchant pick which queue to manage when several exist.
         Hidden when no queue exists (nothing to configure yet). */}
      {!noQueue && (
        <GlobalMessagePanel
          commerce={commerce}
          queues={queues}
          onChange={() => { loadQueues(); load(activeQueueId); api.get("/merchant/me").then((r) => setCommerce(r.data)).catch(() => {}); }}
        />
      )}

      {!noQueue && (
        <QueueSettingsPanel
          queues={queues}
          activeQueueId={activeQueueId}
          onActiveChange={(id) => setActiveQueueId(id)}
          onChange={() => { loadQueues(); load(activeQueueId); }}
        />
      )}

      {/* TV code block — placed AFTER the live ticket controls so the
         establishment can quickly reach the call/present/absent actions
         first (ordre demandé : Essai → Ticket en cours → Aperçu → Logo → Code TV). */}
      <CommerceLogoDialog
        open={logoDialogOpen}
        commerce={commerce}
        onClose={() => setLogoDialogOpen(false)}
        onChange={() => { loadQueues(); load(); }}
      />
      <TVCodeBlock commerceId={commerce?.id} />

      {/* Tickets history — placed at the very bottom of the dashboard.
         `refreshKey` (queue?.tickets count) re-fetches sessions when a new
         ticket is taken / status changes, so freshly-created entries appear
         in the active tab without a manual reload. */}
      <TicketsHistoryPanel refreshKey={queue?.tickets?.length ?? 0} />

      {/* Delete-queue confirmation modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
          onClick={() => setDeleteConfirm(null)}
          data-testid="queue-delete-confirm-backdrop"
        >
          <div
            className="bg-white border-2 border-destructive max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="queue-delete-confirm-modal"
            role="alertdialog"
          >
            <div className="px-5 py-4 border-b border-border bg-destructive/5 flex items-center gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0" />
              <h3 className="font-display text-lg font-black text-ink">Supprimer la file « {deleteConfirm.name} » ?</h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-2">
              <p><strong className="text-destructive">Attention :</strong> vous êtes sur le point de supprimer cette file d'attente.</p>
              <p>Cette action est <strong>définitive</strong>.</p>
              <p>Souhaitez-vous continuer ?</p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-md border border-ink"
                data-testid="queue-delete-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteQueue}
                className="rounded-md bg-destructive hover:bg-destructive/90 text-white"
                data-testid="queue-delete-confirm-btn"
              >
                <Trash2 size={14} className="mr-1.5" /> Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset-queue confirmation */}
      <ConfirmDialog
        open={resetConfirm}
        title="Réinitialiser cette file ?"
        message={
          <>
            <p>Tous les tickets en attente seront <strong>annulés</strong>.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        }
        confirmLabel="OK"
        destructive
        onConfirm={performResetQueue}
        onCancel={() => setResetConfirm(false)}
        testid="queue-reset-confirm"
      />

      {/* Active-tickets warning before deleting a queue */}
      <ConfirmDialog
        open={!!activeTicketsWarn}
        title="File contenant des tickets actifs"
        message={activeTicketsWarn ? (
          <>
            <p>
              La file <strong>« {activeTicketsWarn.name} »</strong> contient
              <strong> {activeTicketsWarn.count} ticket{activeTicketsWarn.count > 1 ? "s" : ""} actif{activeTicketsWarn.count > 1 ? "s" : ""}</strong>.
            </p>
            <p>Avant de pouvoir la supprimer, vous devez d'abord les libérer (les appeler ou réinitialiser la file).</p>
            <p className="font-bold">Voulez-vous réinitialiser la file maintenant&nbsp;?</p>
          </>
        ) : null}
        confirmLabel="Réinitialiser"
        cancelLabel="Annuler"
        destructive
        onConfirm={performResetFromActiveWarn}
        onCancel={() => setActiveTicketsWarn(null)}
        testid="queue-active-warn"
      />

      {/* Rename queue prompt */}
      <PromptDialog
        open={!!renameTarget}
        title="Renommer la file"
        message="Nouveau nom de la file (20 caractères maximum). Enregistré en MAJUSCULES."
        defaultValue={renameTarget?.currentName || ""}
        placeholder="ACCUEIL, CAISSE..."
        maxLength={20}
        uppercase
        confirmLabel="Valider"
        onConfirm={performRenameQueue}
        onCancel={() => setRenameTarget(null)}
        testid="queue-rename"
      />

      {/* First-queue creation prompt — opens when the establishment clicks
         on « Créer ma première file ». The merchant must explicitly choose
         a name (no auto-default). */}
      <PromptDialog
        open={createFirstOpen}
        title="Créer une file d'attente"
        message="Donnez un nom à votre première file (20 caractères maximum). Le nom sera enregistré en MAJUSCULES."
        placeholder="ACCUEIL, CAISSE, DR JAMAL..."
        maxLength={20}
        uppercase
        confirmLabel="Créer la file"
        busy={createFirstBusy}
        onConfirm={performCreateFirstQueue}
        onCancel={() => setCreateFirstOpen(false)}
        testid="create-first-queue-prompt"
      />

      {/* Establishment-wide message — quick-edit dialog opened from the
         "Message" shortcut at the top of the dashboard. */}
      <GlobalMessageDialog
        open={messageDialogOpen}
        commerce={commerce}
        onClose={() => setMessageDialogOpen(false)}
        onSaved={() => { api.get("/merchant/me").then((r) => setCommerce(r.data)).catch(() => {}); }}
      />

      {/* Password-protected confirmation (delete / close queue) per spec §3. */}
      <PasswordConfirmDialog
        open={!!pwdDialog}
        title={pwdDialog?.label || "Confirmation"}
        description={pwdDialog?.description}
        destructive={!!pwdDialog?.destructive}
        confirmLabel={pwdDialog?.confirmLabel || "Confirmer"}
        busy={pwdBusy}
        errorMessage={pwdError}
        onCancel={() => { setPwdDialog(null); setPwdError(""); }}
        onConfirm={performPasswordedAction}
        testid="queue-password-confirm"
      />

      {/* Close-queue confirmation modal — replaces the legacy native window.confirm() */}
      {/* Additional-queue fee confirmation modal (4,90 € TTC per extra
          queue). Shown when /merchant/queues/price-quote reports the
          merchant is about to create a 2nd+ queue. */}
      {feeConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            if (creatingPaidQueue) return;
            // Spec fork 25.16 — clic sur le backdrop = annuler ⇒
            // ferme aussi le formulaire « créer une file » + efface
            // le nom saisi pour revenir à l'état « Ajouter une file ».
            setFeeConfirm(null);
            setShowAddQueue(false);
            setNewQueueName("");
          }}
          data-testid="queue-fee-confirm-backdrop"
        >
          <div
            className="bg-white border-2 border-brand max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="queue-fee-confirm-modal"
            role="alertdialog"
            aria-labelledby="queue-fee-confirm-title"
          >
            <div className="px-5 py-4 border-b border-border bg-brand/5 flex items-center gap-3">
              <Plus size={22} className="text-brand shrink-0" />
              <h3 id="queue-fee-confirm-title" className="font-display text-lg font-black text-ink">
                File supplémentaire&nbsp;: confirmation
              </h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
              <p>
                Votre première file est <strong>incluse dans l'abonnement</strong>. La création d'une file supplémentaire est facturée :
              </p>
              <div className="my-2 rounded-lg border-2 border-brand bg-brand/5 px-4 py-3 text-center" data-testid="queue-fee-amount-block">
                <p className="overline text-[10px] text-brand mb-1">Paiement ponctuel</p>
                <p className="font-mono text-3xl font-black text-brand">
                  {(feeConfirm.feeEur || 0).toFixed(2).replace(".", ",")} € TTC
                </p>
              </div>
              <p className="text-xs text-subtle leading-snug">
                Ce montant sera facturé au prochain cycle de votre abonnement. Vous pourrez créer jusqu'à 10 files au total.
              </p>
              <p className="text-xs text-ink leading-snug bg-[#F18F18]/10 border border-[#F18F18]/40 rounded-md px-3 py-2" data-testid="queue-fee-validity-notice">
                ⏱ <strong>Validité&nbsp;: 24 h</strong> à compter de la création.<br />
                Au-delà, la file termine les tickets en cours puis se ferme automatiquement. Pour la rouvrir, un nouveau paiement de 4,90 € TTC sera demandé.
              </p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap items-center justify-end gap-2">
              <PaymentMethodsBadge className="w-full order-first mb-1" testId="queue-fee-payment-methods" />
              <Button
                variant="outline"
                onClick={() => {
                  // Spec utilisateur fork 25.16 — annuler la confirmation
                  // de paiement doit revenir à l'état « Ajouter une file »
                  // (formulaire fermé, nom de file effacé), pas au champ
                  // pré-rempli avec le nom de la file qu'on n'a finalement
                  // pas créée. Le `feeConfirm` est aussi fermé.
                  setFeeConfirm(null);
                  setShowAddQueue(false);
                  setNewQueueName("");
                }}
                disabled={creatingPaidQueue}
                className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                data-testid="queue-fee-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmPaidQueueCreation}
                disabled={creatingPaidQueue}
                className="rounded-md bg-brand hover:bg-brand-hover text-white uppercase font-bold tracking-wider text-xs px-6"
                data-testid="queue-fee-confirm-btn"
              >
                {creatingPaidQueue ? "Création…" : `Je confirme — payer ${(feeConfirm.feeEur || 0).toFixed(2).replace(".", ",")} €`}
              </Button>
              {/* Spec utilisateur fork 25.18 — bouton « Simuler le
                 paiement » exclusivement pour les comptes test
                 (`isTestAccount=True`, ex. TEST1). Permet de vérifier
                 la chaîne complète post-paiement sans passer par Stripe
                 Checkout : appelle l'endpoint test
                 `/payments/test/simulate-success` avec
                 `purpose=additional_queue`. Le bouton n'apparaît PAS
                 pour les comptes réels en production. */}
              {commerce?.simulatePaymentEnabled && (
                <Button
                  onClick={async () => {
                    if (!feeConfirm) return;
                    setCreatingPaidQueue(true);
                    try {
                      // Spec fork 25.18 — `feeConfirm.pendingName` est
                      // la clé réelle stockée par `createQueue`
                      // (cf. setFeeConfirm({ feeEur, pendingName })).
                      // Lire `feeConfirm.name` renvoyait `undefined`
                      // → backend rejetait avec « Nom de file trop
                      // court ».
                      const res = await simulatePayment("additional_queue", {
                        name: feeConfirm.pendingName,
                      });
                      if (res) {
                        setFeeConfirm(null);
                        setShowAddQueue(false);
                        setNewQueueName("");
                      }
                    } finally {
                      setCreatingPaidQueue(false);
                    }
                  }}
                  disabled={creatingPaidQueue}
                  className="rounded-md bg-[#F18F18] hover:bg-[#d97e10] text-white uppercase font-bold tracking-wider text-xs px-5"
                  data-testid="queue-fee-simulate-btn"
                  title="Test : créer la file sans passer par Stripe"
                >
                  🧪 Simuler
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* First ponctuel queue (7,90 € TTC, 24 h) — modal d'activation
          OU de reprise après fermeture. Spec fork 2026-02. */}
      {ponctuelModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !activatingPonctuel && setPonctuelModal(null)}
          data-testid="ponctuel-modal-backdrop"
        >
          <div
            className="bg-white border-2 border-[#F18F18] max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="ponctuel-modal"
            role="alertdialog"
            aria-labelledby="ponctuel-modal-title"
          >
            <div className="px-5 py-4 border-b border-border bg-[#F18F18]/10 flex items-center gap-3">
              <Plus size={22} className="text-[#F18F18] shrink-0" />
              <h3 id="ponctuel-modal-title" className="font-display text-lg font-black text-ink">
                {ponctuelModal.mode === "reprendre"
                  ? "Reprendre une nouvelle file"
                  : "Activer ma 1ʳᵉ file ponctuelle"}
              </h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
              <p>
                {ponctuelModal.mode === "reprendre"
                  ? "Votre file ponctuelle précédente est arrivée à terme. Pour reprendre une nouvelle file, un paiement ponctuel est requis :"
                  : "Activez votre première file en mode ponctuel — sans engagement et sans abonnement mensuel :"}
              </p>
              <div className="my-2 rounded-lg border-2 border-[#F18F18] bg-[#F18F18]/5 px-4 py-3 text-center">
                <p className="overline text-[10px] text-[#F18F18] mb-1">Paiement ponctuel</p>
                <p className="font-mono text-3xl font-black text-[#F18F18]">7,90 € TTC</p>
                <p className="text-[11px] text-subtle mt-1">Valable 24 h</p>
              </div>
              <label className="block">
                <span className="overline text-[10px] text-subtle mb-1 block">Nom de la file (2 à 20 caractères)</span>
                <Input
                  value={ponctuelName}
                  onChange={(e) => setPonctuelName(e.target.value.toUpperCase().slice(0, 20))}
                  placeholder="ACCUEIL"
                  className="font-mono uppercase"
                  data-testid="ponctuel-modal-name-input"
                  autoFocus
                  maxLength={20}
                />
              </label>
              <p className="text-xs text-ink leading-snug bg-[#F18F18]/10 border border-[#F18F18]/40 rounded-md px-3 py-2">
                ⏱ <strong>Validité&nbsp;: 24 h</strong> à compter du paiement.<br />
                Au-delà, la file termine les tickets en cours puis se ferme automatiquement. Pour reprendre, un nouveau paiement de 7,90 € TTC sera demandé.
              </p>
              <p className="text-[11px] text-subtle leading-snug">
                Tant que cette file est active, vous pouvez créer des files supplémentaires à <strong>4,90 € TTC</strong> chacune (24 h).
              </p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap items-center justify-end gap-2">
              <PaymentMethodsBadge className="w-full order-first mb-1" testId="ponctuel-modal-payment-methods" />
              <Button
                variant="outline"
                onClick={() => setPonctuelModal(null)}
                disabled={activatingPonctuel}
                className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                data-testid="ponctuel-modal-cancel-btn"
              >
                Annuler
              </Button>
              {commerce?.simulatePaymentEnabled && (
                <Button
                  variant="outline"
                  onClick={simulatePonctuelFromModal}
                  disabled={activatingPonctuel || (ponctuelName || "").trim().length < 2}
                  className="rounded-md border-2 border-[#F18F18] text-[#F18F18] hover:bg-[#F18F18]/10 uppercase font-bold tracking-wider text-xs px-4"
                  data-testid="ponctuel-modal-simulate-btn"
                >
                  🧪 Simuler
                </Button>
              )}
              <Button
                onClick={activateFirstPonctuel}
                disabled={activatingPonctuel || (ponctuelName || "").trim().length < 2}
                className="rounded-md bg-[#F18F18] hover:bg-[#d97e10] text-white uppercase font-bold tracking-wider text-xs px-6"
                data-testid="ponctuel-modal-confirm-btn"
              >
                {activatingPonctuel ? "Redirection vers Stripe…" : "Je confirme — payer 7,90 €"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Additional-queue RENEW modal (fork 19) — when 24h expired, the
          merchant can pay 4,90 € TTC again for another 24h. Mirrors the
          create-fee modal style so the UX feels familiar. */}
      {renewConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !renewingQueue && setRenewConfirm(null)}
          data-testid="queue-renew-confirm-backdrop"
        >
          <div
            className="bg-white border-2 border-brand max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="queue-renew-confirm-modal"
            role="alertdialog"
            aria-labelledby="queue-renew-confirm-title"
          >
            <div className="px-5 py-4 border-b border-border bg-brand/5 flex items-center gap-3">
              <Plus size={22} className="text-brand shrink-0" />
              <h3 id="queue-renew-confirm-title" className="font-display text-lg font-black text-ink">
                Renouveler la file « {renewConfirm.name} »&nbsp;?
              </h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
              <p>
                La validité de cette file supplémentaire a expiré (24 h écoulées). Pour la rouvrir, un paiement ponctuel est requis :
              </p>
              <div className="my-2 rounded-lg border-2 border-brand bg-brand/5 px-4 py-3 text-center">
                <p className="overline text-[10px] text-brand mb-1">Paiement ponctuel</p>
                <p className="font-mono text-3xl font-black text-brand">
                  {(4.9).toFixed(2).replace(".", ",")} € TTC
                </p>
              </div>
              <p className="text-xs text-ink leading-snug bg-[#F18F18]/10 border border-[#F18F18]/40 rounded-md px-3 py-2">
                ⏱ <strong>Validité renouvelée&nbsp;: 24 h</strong> à compter de cette confirmation.
              </p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap items-center justify-end gap-2">
              <PaymentMethodsBadge className="w-full order-first mb-1" testId="queue-renew-payment-methods" />
              <Button
                variant="outline"
                onClick={() => setRenewConfirm(null)}
                disabled={renewingQueue}
                className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                data-testid="queue-renew-cancel-btn"
              >
                Annuler
              </Button>
              {/* Spec utilisateur fork 25.17 — bouton « Simuler le paiement »
                 sur la modale de renouvellement des files supplémentaires
                 4,90 €, réservé aux comptes flaggés `isTestAccount=True`
                 (TEST1). Appelle `/payments/test/simulate-success` avec
                 `purpose=queue_renewal` et `queueId` ciblé. Disparaît
                 pour tous les autres comptes en production. */}
              {commerce?.simulatePaymentEnabled && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!renewConfirm) return;
                    setRenewingQueue(true);
                    try {
                      const res = await simulatePayment("queue_renewal", { queueId: renewConfirm.id });
                      if (res) {
                        setRenewConfirm(null);
                      }
                    } finally {
                      setRenewingQueue(false);
                    }
                  }}
                  disabled={renewingQueue}
                  className="rounded-md border-2 border-[#F18F18] text-[#F18F18] hover:bg-[#F18F18]/10 uppercase font-bold tracking-wider text-xs px-4"
                  data-testid="queue-renew-simulate-btn"
                  title="Test : renouveler la file sans passer par Stripe"
                >
                  🧪 Simuler
                </Button>
              )}
              <Button
                onClick={performRenewAdditional}
                disabled={renewingQueue}
                className="rounded-md bg-brand hover:bg-brand-hover text-white uppercase font-bold tracking-wider text-xs px-6"
                data-testid="queue-renew-confirm-btn"
              >
                {renewingQueue ? "Redirection vers Stripe…" : "Je confirme — payer 4,90 €"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {closeConfirm && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
          onClick={() => setCloseConfirm(false)}
          data-testid="queue-close-confirm-backdrop"
        >
          <div
            className="bg-white border-2 border-destructive max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="queue-close-confirm-modal"
            role="alertdialog"
            aria-labelledby="queue-close-confirm-title"
          >
            <div className="px-5 py-4 border-b border-border bg-destructive/5 flex items-center gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0" />
              <h3 id="queue-close-confirm-title" className="font-display text-lg font-black text-ink">
                Fermer cette file d'attente&nbsp;?
              </h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
              <p>
                Cette action <strong>réinitialisera automatiquement le compteur à zéro</strong>.
                Les tickets déjà attribués ne seront plus valables.
              </p>
              <p className="text-destructive font-bold">
                Cette opération est irréversible.
              </p>
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCloseConfirm(false)}
                className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                data-testid="queue-close-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmCloseQueue}
                className="rounded-md bg-destructive hover:bg-destructive/90 text-white uppercase font-bold tracking-wider text-xs px-6"
                data-testid="queue-close-confirm-btn"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DANGER ZONE — discreet "Supprimer mon compte" link.
          Anchored at the very bottom of the dashboard per 2026-02 spec.
          Opens a modal that asks for the password and a final confirmation
          before hard-deleting the establishment + blacklisting it. */}
      <DeleteAccountSection commerce={commerce} freePeriodEnd={freePeriodEnd} />
    </div>
  );
}

/** Discreet account-deletion section + modal. Lives at the bottom of the
 *  dashboard. The button is intentionally low-contrast and small — this is
 *  a deliberately friction-heavy flow.
 *
 *  Spec utilisateur fork 24 :
 *  - Pré-2027 : suppression immédiate, message simple, pas de remboursement.
 *  - Post-2027 + abonnement actif : suppression DIFFÉRÉE à la fin de
 *    l'abonnement payé. Pas de remboursement.
 *  - Post-2027 sans abonnement : suppression immédiate, message mentionne
 *    le non-remboursement pour les paiements ponctuels (7,90 € / 4,90 €).
 */
function DeleteAccountSection({ commerce, freePeriodEnd }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reset = () => { setOpen(false); setConfirming(false); setPassword(""); };

  // Détermine le contexte d'affichage à partir du commerce et de la date.
  const isFreePeriod = !freePeriodEnd || new Date() <= new Date(freePeriodEnd);
  const subEnd = commerce?.subscriptionEndDate ? new Date(commerce.subscriptionEndDate) : null;
  const hasActiveSub = commerce?.subscriptionStatus === "active" && subEnd && subEnd > new Date();
  const willBeDeferred = !isFreePeriod && hasActiveSub;
  const subEndLabel = subEnd ? subEnd.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "";

  const submit = async () => {
    if (!password) { toast.error("Veuillez saisir votre mot de passe."); return; }
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    try {
      const { data } = await api.post("/merchant/account/delete", { password });
      if (data?.deferred) {
        toast.success("Suppression programmée à la fin de votre abonnement payé. Votre compte reste accessible jusqu'à cette échéance.", { duration: 8000 });
        reset();
        // Recharger le state — pas de logout puisque le compte reste accessible.
        window.location.reload();
        return;
      }
      toast.success("Votre compte a bien été supprimé.", { duration: 6000 });
      logout();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression du compte.");
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="mt-16 mb-4 flex justify-center" data-testid="delete-account-section">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] text-subtle hover:text-destructive underline-offset-2 hover:underline transition-colors"
          data-testid="delete-account-trigger-btn"
        >
          Supprimer mon compte
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={reset}
          data-testid="delete-account-backdrop"
        >
          <div
            className="bg-white border-2 border-destructive max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-account-modal"
            role="alertdialog"
            aria-labelledby="delete-account-title"
          >
            <div className="px-5 py-4 border-b border-border bg-destructive/5 flex items-start gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0 mt-0.5" />
              <h3 id="delete-account-title" className="font-display text-lg font-black text-ink leading-tight">
                {willBeDeferred ? "Suppression programmée du compte" : "Suppression définitive du compte"}
              </h3>
            </div>
            <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3" data-testid="delete-account-body">
              {isFreePeriod ? (
                <p>
                  La suppression de votre compte entraînera la <strong>suppression définitive</strong> de vos files d'attente, de vos tickets, de votre historique et de vos paramètres. <strong>Cette action est irréversible.</strong>
                </p>
              ) : willBeDeferred ? (
                <>
                  <p>
                    Vous disposez actuellement d'un <strong>abonnement actif</strong> jusqu'au <strong>{subEndLabel}</strong>.
                  </p>
                  <p>
                    Votre compte restera accessible jusqu'à cette échéance (files, tableau de bord). <strong>À l'échéance, le compte sera automatiquement supprimé</strong> et toutes vos données seront définitivement effacées.
                  </p>
                  <p className="text-xs text-subtle">
                    Aucun remboursement total ou partiel de la période déjà payée ne sera effectué.
                  </p>
                </>
              ) : (
                <p>
                  La suppression de votre compte entraînera la <strong>suppression définitive</strong> de vos files d'attente, de vos tickets, de votre historique et de vos paramètres. <strong>Cette action est irréversible.</strong>
                </p>
              )}
              {!isFreePeriod && (
                <p className="text-xs text-ink leading-snug bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  Si vous avez acheté une file ponctuelle à <strong>7,90 €</strong> ou une ou plusieurs files supplémentaires à <strong>4,90 €</strong>, celles-ci seront définitivement supprimées et <strong>aucun remboursement ne sera effectué</strong>.
                </p>
              )}
              <p className="text-xs text-ink leading-snug bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2" data-testid="delete-account-tickets-warning">
                <strong>Attention :</strong> la suppression de votre compte entraînera également l'<strong>annulation immédiate de tous les tickets actuellement détenus par vos visiteurs</strong>.
              </p>
              <div>
                <label className="text-xs font-bold text-ink uppercase tracking-wider">Confirmez avec votre mot de passe</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full h-11 px-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-destructive/30"
                  placeholder="Votre mot de passe"
                  data-testid="delete-account-password-input"
                />
              </div>
              {confirming && (
                <p className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-destructive text-xs font-bold leading-snug" data-testid="delete-account-final-confirm">
                  Êtes-vous certain de vouloir supprimer définitivement votre compte ? Cette action est irréversible.
                </p>
              )}
            </div>
            <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={reset}
                disabled={deleting}
                className="rounded-md border border-ink uppercase font-bold tracking-wider text-xs px-5"
                data-testid="delete-account-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={deleting}
                className="rounded-md bg-destructive hover:bg-destructive/90 text-white uppercase font-bold tracking-wider text-xs px-6"
                data-testid="delete-account-confirm-btn"
              >
                {deleting ? "Suppression…" : (confirming ? (willBeDeferred ? "Programmer la suppression" : "Supprimer définitivement") : "Continuer")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * BigSwitch — pill-shaped toggle, larger than the default shadcn Switch for
 * better mobile ergonomics. Positioned absolutely top-right inside its parent
 * card. Color of the active state can be customised via `activeBg`.
 */
function BigSwitch({ checked, onChange, activeBg = "#0A7F2E", testid, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      data-testid={testid}
      className="absolute top-3 right-3 inline-flex items-center h-6 w-11 sm:h-7 sm:w-12 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand/50"
      style={{ backgroundColor: checked ? activeBg : "#D1D5DB" }}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px] sm:translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/**
 * QueueRow — list-style queue item used inside the "Files d'attente" block.
 * Shows index, status dot, name, status pill, creation date and action buttons.
 * Selecting the row updates the active queue used by the "Ticket en cours" panel.
 *
 * Display ON/OFF is controlled here via a modern iOS/Android-style switch
 * (auto-save, no save button needed). The info button surfaces tooltips
 * describing each action — both on desktop hover and on mobile tap.
 */
function QueueRow({ index, queue, active, onSelect, onRename, onDelete, onToggleDisplay }) {
  const status = queue.isOpen ? (queue.isSuspended ? "suspended" : "open") : "closed";
  const dot = { open: "bg-[#0A7F2E]", suspended: "bg-[#F18F18]", closed: "bg-destructive" }[status];
  const pill = {
    open:      { label: "Ouverte",   cls: "bg-[#0A7F2E]/15 text-[#0A7F2E]" },
    suspended: { label: "Suspendue", cls: "bg-[#F18F18]/15 text-[#A85C00]" },
    closed:    { label: "Fermée",    cls: "bg-destructive/15 text-destructive" },
  }[status];
  const displayOn = queue.displayOnPublic !== false;
  const [showInfo, setShowInfo] = useState(false);
  const [bubblePos, setBubblePos] = useState({ top: 0, left: 0 });
  const infoBtnRef = useRef(null);

  // Close the info tooltip when clicking outside or scrolling the page —
  // the bubble is fixed-positioned so it would otherwise stay in place while
  // the underlying button moves with the scroll.
  useEffect(() => {
    if (!showInfo) return;
    const close = () => setShowInfo(false);
    const t = setTimeout(() => {
      document.addEventListener("click", close);
      window.addEventListener("scroll", close, true); // capture phase → catches nested scroll containers too
      window.addEventListener("resize", close);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [showInfo]);

  const created = (() => {
    try {
      const d = new Date(queue.createdAt);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mn = String(d.getMinutes()).padStart(2, "0");
      return `Créée le ${dd}/${mm}/${yy} à ${hh}:${mn}`;
    } catch { return null; }
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className={`flex flex-col sm:flex-row sm:items-center gap-2 px-2 sm:px-3 py-2 transition-colors cursor-pointer ${active ? "bg-brand/5" : "hover:bg-surface/50"}`}
      data-testid={`queue-tab-${queue.id}`}
    >
      {/* Row 1 (mobile + desktop) — index, status dot, name, status pill */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md border-2 ${active ? "border-brand text-brand" : "border-border text-subtle"} font-mono font-bold text-xs`}>
          {index}
        </span>
        <span className={`shrink-0 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${dot}`} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-ink text-sm leading-tight truncate" title={queue.name}>{queue.name}</span>
            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${pill.cls}`}>{pill.label}</span>
            {/* Spec utilisateur fork 25.22 + fork 26 — badge EXPIRÉ à côté
               de FERMÉE quand la file est expirée (24 h, abonnement expiré,
               cutover 2027). On couvre désormais aussi la phase TRANSITOIRE
               `drainMode=True` (file fermée avec tickets encore à traiter)
               afin que l'établissement voie clairement que sa file a expiré
               et qu'il doit finir d'évacuer les tickets restants. */}
            {(["expired_24h", "post_free_period_2027", "subscription_expired_keep_alive"].includes(queue.closedReason) ||
              (queue.drainMode && ["expired_24h", "post_free_period_2027", "subscription_expired_keep_alive"].includes(queue.drainReason))) && (
              <span
                className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-destructive text-white"
                data-testid={`queue-row-expired-pill-${queue.id}`}
              >
                Expiré
              </span>
            )}
          </div>
          {created && <p className="text-[10px] text-subtle mt-0.5 leading-tight truncate">{created}</p>}
        </div>
      </div>

      {/* Row 2 (mobile) / inline (desktop) — switch + actions, right-aligned */}
      <div className="flex items-center gap-2 justify-end shrink-0 pl-9 sm:pl-0">

      {/* Spec utilisateur fork 25.34 — l'ancien interrupteur on/off
         est remplacé par un bouton ÉCRAN explicite avec icône TV, plus
         clair pour le merchant : un clic bascule la diffusion sur
         l'écran public. État visuel :
           • Diffusion ACTIVE → fond bleu BypassGo + petite pastille verte
             en haut à droite.
           • Diffusion INACTIVE → fond blanc + bord gris + petite pastille
             rouge.
         Mêmes data-testid que l'ancien switch pour ne pas casser les
         tests existants. */}
      <button
        type="button"
        role="switch"
        aria-checked={displayOn}
        aria-label={displayOn ? "Diffusion sur l'écran public activée" : "Diffusion sur l'écran public désactivée"}
        onClick={(e) => { e.stopPropagation(); onToggleDisplay(!displayOn); }}
        title={displayOn ? "Diffusée sur l'écran public — cliquer pour masquer" : "Masquée de l'écran public — cliquer pour diffuser"}
        className={`shrink-0 relative inline-flex flex-col items-center justify-center w-11 h-7 rounded-md border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand/40 ${
          displayOn
            ? "bg-brand border-brand text-white"
            : "bg-white border-border text-subtle hover:border-ink hover:text-ink"
        }`}
        data-testid={`queue-display-switch-${queue.id}`}
      >
        <Tv size={14} strokeWidth={2.4} />
        <span
          className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${
            displayOn ? "bg-[#0A7F2E]" : "bg-destructive"
          }`}
          aria-hidden="true"
        />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onRename(); }}
        className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-border text-subtle hover:border-ink hover:text-ink"
        title="Modifier le nom de la file"
        data-testid={`queue-rename-${queue.id}`}
      >
        <Pencil size={12} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); if (!queue.isDefault) onDelete(); }}
        disabled={queue.isDefault}
        className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border ${queue.isDefault ? "border-border text-border cursor-not-allowed" : "border-destructive/40 text-destructive hover:bg-destructive hover:text-white"}`}
        title={queue.isDefault ? "La file principale ne peut pas être supprimée" : "Suppression définitive de la file (⚠️ ACTION IRRÉVERSIBLE)"}
        data-testid={`queue-delete-${queue.id}`}
      >
        <Trash2 size={12} />
      </button>

      {/* ⓘ Info button — opens an inline bubble listing the function legends.
         Style: matches the red coral ⓘ button next to the establishment name
         (EstablishmentInfoTooltip) — same red ring, same compact bubble. */}
      <div className="shrink-0 relative">
        <button
          ref={infoBtnRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (showInfo) { setShowInfo(false); return; }
            const r = e.currentTarget.getBoundingClientRect();
            // Pop the bubble ABOVE the button (parent has overflow-hidden
            // for rounded corners, so we render via fixed positioning to
            // escape that clipping context).
            const bubbleW = 288;
            const margin = 8;
            const left = Math.min(window.innerWidth - bubbleW - margin, Math.max(margin, r.right - bubbleW));
            setBubblePos({ top: r.top - margin, left });
            setShowInfo(true);
          }}
          className="group inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border transition-all duration-200 ease-out shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(229,72,77,0.18)] hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{ borderColor: "#E5484D", color: "#E5484D" }}
          title="Aide / Informations"
          data-testid={`queue-info-${queue.id}`}
          aria-label="Légendes des fonctions"
        >
          <span
            aria-hidden="true"
            className="font-serif italic font-semibold leading-none select-none transition-transform duration-200 group-hover:scale-110"
            style={{ fontSize: 14, color: "#E5484D" }}
          >
            i
          </span>
        </button>
        {showInfo && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ top: bubblePos.top, left: bubblePos.left, transform: "translateY(-100%)" }}
            className="fixed z-[70] w-72 p-3 bg-white rounded-xl border border-border shadow-[0_10px_30px_rgba(15,23,42,0.12)] text-left animate-in fade-in zoom-in-95 duration-200"
            role="dialog"
            aria-label="Légendes des fonctions"
            data-testid={`queue-info-bubble-${queue.id}`}
          >
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-subtle mb-2 px-0.5">
              Légendes des fonctions
            </p>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2 text-[12px] leading-snug text-ink" data-testid={`queue-info-item-interrupteur-${queue.id}`}>
                <Tv size={14} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="font-bold">Bouton Écran :</span>{" "}
                  <span className="text-subtle">
                    Diffuse la file sur l'écran public. Pastille verte = diffusée,
                    pastille rouge = masquée. Cliquez pour basculer.
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-2 text-[12px] leading-snug text-ink" data-testid={`queue-info-item-crayon-${queue.id}`}>
                <Pencil size={14} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="font-bold">Crayon :</span>{" "}
                  <span className="text-subtle">Modifier le nom de la file.</span>
                </span>
              </li>
              <li className="flex items-start gap-2 text-[12px] leading-snug text-ink" data-testid={`queue-info-item-corbeille-${queue.id}`}>
                <Trash2 size={14} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="font-bold">Corbeille :</span>{" "}
                  <span className="text-subtle">Suppression définitive de la file </span>
                  <span className="text-destructive font-bold">(⚠️ ACTION IRRÉVERSIBLE)</span>
                  <span className="text-subtle">.</span>
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function OvStat({ icon, label, value, hint, border, testid }) {
  return (
    <div
      className={`p-4 bg-white ${border ? "border-l border-border" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-subtle">
        {icon} {label}
      </div>
      <p className="mt-2 font-mono text-2xl md:text-3xl font-bold text-ink leading-none">{value}</p>
      {hint && <p className="mt-1 text-[10px] text-subtle italic">{hint}</p>}
    </div>
  );
}


function Stat({ label, value, icon, border }) {
  return (
    <div className={`p-5 ${border ? "border-l border-border" : ""}`}>
      <p className="overline flex items-center gap-1.5">{icon} {label}</p>
      <p className="mt-2 font-mono text-3xl font-bold text-ink">{value}</p>
    </div>
  );
}

function SubscriptionBanner({ commerce, daysLeft, onSubscribe, onExtendPonctuel, freePeriodEnd, queues }) {
  if (!commerce) return null;
  // Format helper : "04/07/2026 à 23:59"
  const formatDateTimeFR = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString("fr-FR");
      const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
      return `${date} à ${time}`;
    } catch { return ""; }
  };
  // Spec utilisateur fork 26 — animation rouge clignotant (2 s / 2 s)
  // quand l'échéance est proche : ≤ 24 h pour l'abonnement, ≤ 1 h pour
  // la file ponctuelle.
  const blinkStyle = (
    <style>{`@keyframes bpg-sub-blink-kf { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0.25; } }
      .bpg-sub-blink-red { animation: bpg-sub-blink-kf 4s ease-in-out infinite; }`}</style>
  );
  // Mode ponctuel (fork 2026-02) — PRIORITÉ sur `subscriptionStatus` :
  // tant que le commerce a explicitement choisi le mode ponctuel
  // (`planType=ponctuel`), on affiche le bandeau ponctuel, même si un
  // résidu d'abonnement précédent traîne dans `subscriptionStatus`.
  if (commerce.planType === "ponctuel") {
    const ponctuelQ = (queues || []).find((q) => q.isFirstPonctuel && q.additionalExpiresAt);
    if (ponctuelQ) {
      const expiresAt = new Date(ponctuelQ.additionalExpiresAt);
      const msLeft = expiresAt.getTime() - Date.now();
      // Spec utilisateur fork 26 — file ponctuelle EXPIRÉE mais
      // toujours visible (tickets restants à traiter avant la
      // redirection vers l'écran de choix) → message d'alerte rouge
      // SANS clignotement remplaçant le bandeau d'activité.
      const isExpired = msLeft <= 0 || ponctuelQ.drainMode || ponctuelQ.closedReason === "expired_24h";
      if (isExpired) {
        return (
          <div
            className="mb-6 border border-[#D7263D] bg-[#FFE7EA] p-4 text-sm"
            data-testid="sub-banner"
          >
            <strong className="text-[#D7263D]">File ponctuelle inactive.</strong>{" "}
            <span className="text-ink">Pour créer une nouvelle file, veuillez d'abord clôturer les tickets en attente sur la file principale ou sur vos autres files.</span>
          </div>
        );
      }
      const urgent = msLeft <= 60 * 60 * 1000; // ≤ 1 heure
      // Spec utilisateur Feb 2026 — le bouton « Prolonger » apparaît 2 h
      // avant l'expiration de la file ponctuelle (7,90 € pour 24 h).
      const canExtend = msLeft > 0 && msLeft <= 2 * 60 * 60 * 1000;
      const classes = urgent
        ? "mb-6 border border-[#D7263D] bg-[#FFE7EA] p-4 text-sm bpg-sub-blink-red"
        : "mb-6 border border-[#34C759]/40 bg-[#ECFBEF] p-4 text-sm";
      const labelColor = urgent ? "text-[#D7263D]" : "text-[#0A7F2E]";
      return (
        <>
          {blinkStyle}
          <div className={classes} data-testid="sub-banner">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <strong className={labelColor}>File ponctuelle actif 24 h</strong>, jusqu'au {formatDateTimeFR(ponctuelQ.additionalExpiresAt)}
              </div>
              {canExtend && onExtendPonctuel && (
                <button
                  type="button"
                  onClick={() => onExtendPonctuel(ponctuelQ.id)}
                  className="shrink-0 inline-flex items-center justify-center gap-2 h-10 px-4 bg-[#1D2D7B] hover:bg-[#162466] text-white text-xs font-bold uppercase tracking-wider rounded-md"
                  data-testid="extend-ponctuel-btn"
                >
                  Prolonger maintenant +24 h (7,90 €)
                </button>
              )}
            </div>
          </div>
        </>
      );
    }
    // Fallback : ponctuel sélectionné mais aucune file ponctuelle active
    // (cas transitoire entre deux paiements).
    return (
      <div className="mb-6 border border-[#F18F18]/50 bg-[#FFF6E8] p-4 text-sm flex items-center gap-3" data-testid="sub-banner">
        <span aria-hidden="true" className="text-2xl">⚡</span>
        <div>
          <p className="font-bold text-[#A65B00]">Mode ponctuel actif</p>
          <p className="text-xs text-subtle">Paiement à la file (7,90 € TTC / 24 h). Files supplémentaires&nbsp;: 4,90 € TTC chacune.</p>
        </div>
      </div>
    );
  }
  // Display a calm, non-monetized banner during the free trial period.
  // The admin (manually) controls whether subscriptions are enabled — no automation here.
  if (commerce.subscriptionStatus === "active") {
    const subEnd = commerce.subscriptionEndDate ? new Date(commerce.subscriptionEndDate) : null;
    const msLeft = subEnd ? subEnd.getTime() - Date.now() : Infinity;
    // Spec utilisateur fork 26 — abonnement EXPIRÉ mais console encore
    // accessible (tickets restants à traiter en drainMode) → message
    // d'alerte rouge SANS clignotement remplaçant le bandeau d'activité.
    const mainQ = (queues || []).find((q) => q.isDefault && !q.isAdditional);
    const isExpired = msLeft <= 0
      || (mainQ && (mainQ.drainMode || mainQ.closedReason === "subscription_expired_keep_alive"));
    if (isExpired) {
      return (
        <div
          className="mb-6 border border-[#D7263D] bg-[#FFE7EA] p-4 text-sm"
          data-testid="sub-banner"
        >
          <strong className="text-[#D7263D]">Votre abonnement a expiré.</strong>{" "}
          <span className="text-ink">Pour créer une nouvelle file, veuillez d'abord clôturer les tickets en attente sur la file principale ou sur vos autres files.</span>
        </div>
      );
    }
    const urgent = msLeft <= 24 * 60 * 60 * 1000; // ≤ 1 jour
    // Spec utilisateur Feb 2026 — le bouton « Prolonger maintenant »
    // apparaît 24 h avant l'expiration de l'abonnement 9,90 €. Le backend
    // top-up la date d'expiration (max(now, current) + 30 jours).
    const canExtend = msLeft > 0 && msLeft <= 24 * 60 * 60 * 1000;
    const classes = urgent
      ? "mb-6 border border-[#D7263D] bg-[#FFE7EA] p-4 text-sm bpg-sub-blink-red"
      : "mb-6 border border-[#34C759]/40 bg-[#ECFBEF] p-4 text-sm";
    const labelColor = urgent ? "text-[#D7263D]" : "text-[#0A7F2E]";
    return (
      <>
        {blinkStyle}
        <div className={classes} data-testid="sub-banner">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <strong className={labelColor}>Abonnement actif 30 jours</strong> jusqu'au {formatDateTimeFR(commerce.subscriptionEndDate)}
            </div>
            {canExtend && onSubscribe && (
              <button
                type="button"
                onClick={onSubscribe}
                className="shrink-0 inline-flex items-center justify-center gap-2 h-10 px-4 bg-[#1D2D7B] hover:bg-[#162466] text-white text-xs font-bold uppercase tracking-wider rounded-md"
                data-testid="extend-subscription-btn"
              >
                Prolonger maintenant +30 jours (9,90 €)
              </button>
            )}
          </div>
        </div>
      </>
    );
  }
  // Spec utilisateur 2026-02 (fork courant) : la gratuité 6-mois-par-compte
  // est remplacée par une gratuité GLOBALE jusqu'à la date FREE_UNTIL
  // configurable côté admin (`/api/admin/free-period`). À partir du
  // lendemain → abonnement 9,90 €/mois TTC.
  const FREE_UNTIL = freePeriodEnd || new Date("2026-12-31T23:59:59Z");
  // L'alerte « ton plus pressant » bascule à J-30 avant la fin (au lieu
  // de la date fixe 01/12/2026, ce qui rend l'UI cohérente avec une
  // date admin déplacée).
  const FIRST_ALERT = new Date(FREE_UNTIL.getTime() - 30 * 86400000);
  const now = new Date();
  const endLabel = FREE_UNTIL.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  if (now <= FREE_UNTIL) {
    const isUrgent = now >= FIRST_ALERT; // ton plus pressant à J-30
    const daysToEnd = Math.max(0, Math.ceil((FREE_UNTIL - now) / 86400000));
    return (
      <div
        className={`mb-6 border p-4 flex items-center gap-3 ${
          isUrgent ? "border-[#D7263D] bg-[#FFF5F6]" : "border-border bg-surface/40"
        }`}
        data-testid="sub-banner"
      >
        {isUrgent ? (
          <span aria-hidden="true" className="text-[#D7263D] text-2xl md:text-3xl leading-none shrink-0" data-testid="sub-banner-warning-icon">⚠️</span>
        ) : (
          <span aria-hidden="true" className="text-3xl md:text-4xl leading-none shrink-0">🎁</span>
        )}
        <div>
          <p className={`text-sm font-bold ${isUrgent ? "text-[#D7263D]" : "text-ink"}`}>
            {isUrgent
              ? `Plus que ${daysToEnd} jour${daysToEnd > 1 ? "s" : ""} de gratuité — fin le ${endLabel}`
              : `Application gratuite jusqu'au ${endLabel}`}
          </p>
          <p className="text-xs text-subtle">
            {isUrgent
              ? "Au-delà : abonnement 9,90 €/mois TTC — sans engagement."
              : "Au-delà : abonnement 9,90 €/mois TTC sans engagement. Vous serez prévenu(e) par email avant cette échéance."}
          </p>
        </div>
      </div>
    );
  }
  // Fallback (post FREE_UNTIL + pas encore d'abonnement actif).
  // Spec utilisateur : pour les comptes créés APRÈS le 31/12/2026 (donc
  // qui n'ont jamais bénéficié de la gratuité), masquer entièrement le
  // bandeau rouge « La période de gratuité est terminée » — c'est trompeur.
  // Le SubscriptionLockScreen (modal plein écran) s'affiche déjà pour
  // proposer le choix de formule. Pour les comptes pré-2027, on garde
  // le bandeau historique mais SANS le fond rouge sur le pictogramme.
  const CUTOVER_2027 = new Date("2027-01-01T00:00:00Z");
  const createdAt = commerce.createdAt ? new Date(commerce.createdAt) : null;
  const createdPost2027 = createdAt && createdAt >= CUTOVER_2027;
  if (createdPost2027) return null;
  return (
    <div className="mb-6 border border-[#D7263D] bg-[#FFF5F6] p-4 flex items-center gap-3" data-testid="sub-banner">
      <span aria-hidden="true" className="text-[#D7263D] text-2xl md:text-3xl leading-none shrink-0" data-testid="sub-banner-warning-icon">⚠️</span>
      <div>
        <p className="text-sm font-bold text-[#D7263D]">La période de gratuité est terminée</p>
        <p className="text-xs text-subtle">
          Choisissez votre formule&nbsp;: abonnement <strong>9,90 €/mois</strong> TTC sans engagement, ou mode ponctuel <strong>7,90 €/file</strong> (24 h).
        </p>
      </div>
    </div>
  );
}


/**
 * ScheduleOpenRow — Spec utilisateur fork 25.27.
 * Ligne « Programmer votre file » insérée dans la Console de gestion juste
 * sous le sélecteur de file. Uniquement pour la file principale incluse
 * dans l'abonnement 9,90 €/mois. Tous comptes, existants + futurs.
 */
function ScheduleOpenRow({ queue, onScheduled, commerce }) {
  const [editing, setEditing] = useState(false);
  const [pickerValue, setPickerValue] = useState("");
  const [busy, setBusy] = useState(false);
  // Spec utilisateur fork 25.28 — popup de confirmation avant annulation.
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Spec utilisateur fork 25.32 — popup d'info non bloquant quand la date
  // de programmation est postérieure à la fin d'abonnement.
  const [postSubInfo, setPostSubInfo] = useState(null);
  const inputRef = useRef(null);

  const scheduled = queue?.scheduledOpenAt
    ? (() => {
        try {
          const d = new Date(queue.scheduledOpenAt);
          return d.toLocaleString("fr-FR", {
            weekday: "long", day: "2-digit", month: "long",
            hour: "2-digit", minute: "2-digit",
          });
        } catch { return null; }
      })()
    : null;

  const openPicker = () => {
    setEditing(true);
    // Spec fork 25.31 — pré-remplir le picker avec :
    //   1. la programmation existante (si on est en mode "Modifier"), pour
    //      éviter qu'un clic involontaire sur "Valider" renvoie une valeur
    //      stale d'une session précédente,
    //   2. sinon, l'heure ronde suivante (création initiale).
    // Cette mise à jour s'applique à chaque ouverture du picker.
    let initial = "";
    if (queue?.scheduledOpenAt) {
      try {
        const existing = new Date(queue.scheduledOpenAt);
        if (!Number.isNaN(existing.getTime())) {
          const pad = (n) => String(n).padStart(2, "0");
          initial = `${existing.getFullYear()}-${pad(existing.getMonth() + 1)}-${pad(existing.getDate())}T${pad(existing.getHours())}:${pad(existing.getMinutes())}`;
        }
      } catch {}
    }
    if (!initial) {
      const d = new Date(Date.now() + 60 * 60 * 1000);
      d.setMinutes(0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      initial = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setPickerValue(initial);
    setTimeout(() => {
      try { inputRef.current?.showPicker?.(); inputRef.current?.focus(); } catch {}
    }, 30);
  };

  const submitSchedule = async () => {
    if (!pickerValue) return;
    let isoWhen;
    try {
      const local = new Date(pickerValue);
      if (Number.isNaN(local.getTime())) throw new Error("invalid");
      isoWhen = local.toISOString();
    } catch {
      toast.error("Date/heure invalide.");
      return;
    }
    if (new Date(isoWhen).getTime() <= Date.now()) {
      toast.error("Veuillez choisir une date dans le futur.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/merchant/queues/${queue.id}/schedule-open`, {
        scheduledOpenAt: isoWhen,
      });
      toast.success("Ouverture programmée enregistrée.");
      setEditing(false);
      // Spec utilisateur fork 25.32 — popup d'INFORMATION non bloquant si
      // la date programmée tombe au-delà de la fin d'abonnement actuelle.
      // La programmation est bien enregistrée, mais elle ne se déclenchera
      // que si l'établissement renouvelle son abonnement avant.
      try {
        const subEnd = commerce?.subscriptionEndDate
          ? new Date(commerce.subscriptionEndDate).getTime()
          : null;
        const scheduledTs = new Date(isoWhen).getTime();
        if (subEnd && Number.isFinite(scheduledTs) && scheduledTs > subEnd) {
          setPostSubInfo(
            new Date(commerce.subscriptionEndDate).toLocaleDateString("fr-FR", {
              weekday: "long", day: "2-digit", month: "long", year: "numeric",
            })
          );
        }
      } catch {}
      onScheduled?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Programmation impossible");
    } finally {
      setBusy(false);
    }
  };

  const cancelSchedule = async () => {
    setBusy(true);
    try {
      await api.delete(`/merchant/queues/${queue.id}/schedule-open`);
      toast.success("Programmation annulée.");
      setConfirmCancel(false);
      onScheduled?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Annulation impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-3 sm:mb-4 rounded-lg border-2 border-brand/30 bg-brand/5 px-3 py-2.5 flex flex-wrap items-center gap-2"
      data-testid="schedule-open-row"
    >
      <CalendarClock size={16} className="text-brand shrink-0" />
      {scheduled && !editing ? (
        <>
          <p className="text-sm font-bold text-ink leading-tight flex-1 min-w-[140px]">
            Ouverture programmée&nbsp;:{" "}
            <span className="font-display font-black text-brand">{scheduled}</span>
          </p>
          <button
            type="button"
            onClick={openPicker}
            disabled={busy}
            className="text-[11px] uppercase font-bold tracking-wider px-3 h-8 rounded-md border border-brand text-brand hover:bg-brand hover:text-white transition-colors"
            data-testid="schedule-open-edit-btn"
          >
            Modifier
          </button>
          <button
            type="button"
            onClick={() => setConfirmCancel(true)}
            disabled={busy}
            className="text-[11px] uppercase font-bold tracking-wider px-3 h-8 rounded-md border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors"
            data-testid="schedule-open-cancel-btn"
          >
            Annuler
          </button>
        </>
      ) : editing ? (
        <>
          <span className="text-[11px] uppercase font-bold tracking-wider text-subtle">
            Date et heure&nbsp;:
          </span>
          <input
            ref={inputRef}
            type="datetime-local"
            value={pickerValue}
            min={(() => {
              const d = new Date();
              const pad = (n) => String(n).padStart(2, "0");
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            })()}
            onChange={(e) => setPickerValue(e.target.value)}
            className="h-8 px-2 rounded-md border border-border bg-white text-ink text-sm font-bold"
            data-testid="schedule-open-input"
          />
          <button
            type="button"
            onClick={submitSchedule}
            disabled={busy || !pickerValue}
            className="text-[11px] uppercase font-bold tracking-wider px-3 h-8 rounded-md bg-[#0A7F2E] text-white hover:bg-[#086822] disabled:opacity-50"
            data-testid="schedule-open-confirm-btn"
          >
            {busy ? "…" : "Valider"}
          </button>
          {/* Spec utilisateur fork 25.28 — « Fermer » est renommé « Annuler ».
             En mode édition (pas encore validée), Annuler ferme simplement
             le picker sans confirmation (rien n'a encore été enregistré). */}
          <button
            type="button"
            onClick={() => { setEditing(false); setPickerValue(""); }}
            disabled={busy}
            className="text-[11px] uppercase font-bold tracking-wider px-3 h-8 rounded-md border border-ink text-ink"
            data-testid="schedule-open-discard-btn"
          >
            Annuler
          </button>
        </>
      ) : (
        <>
          <p className="text-sm font-bold text-ink leading-tight flex-1 min-w-[140px]">
            Programmer votre file&nbsp;:{" "}
            <span className="font-normal text-subtle">choisissez la date et l'heure d'ouverture.</span>
          </p>
          <button
            type="button"
            onClick={openPicker}
            className="text-[11px] uppercase font-bold tracking-wider px-3 h-8 rounded-md bg-brand text-white hover:bg-brand-hover"
            data-testid="schedule-open-open-picker-btn"
          >
            Choisir une date
          </button>
        </>
      )}

      {/* Spec utilisateur fork 25.28 — popup de confirmation oui/non
         affichée quand on annule une programmation déjà active. */}
      {confirmCancel && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
          data-testid="schedule-open-cancel-confirm-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setConfirmCancel(false); }}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-cancel-title"
          >
            <div className="px-5 py-4 border-b border-border">
              <h3
                id="schedule-cancel-title"
                className="font-display font-black uppercase tracking-[0.04em] text-base text-ink"
              >
                Annuler la programmation ?
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-ink leading-snug">
              <p>
                Souhaitez-vous vraiment annuler l'ouverture programmée de votre
                file&nbsp;? Une fois annulée, la file restera <strong>ouverte sans
                limite</strong>, jusqu'à atteinte du quota éventuel ou jusqu'à
                fermeture manuelle de votre établissement.
              </p>
            </div>
            <div className="px-5 py-3 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmCancel(false)}
                disabled={busy}
                className="text-xs uppercase font-bold tracking-wider px-4 h-9 rounded-md border border-ink text-ink"
                data-testid="schedule-open-cancel-confirm-no"
              >
                Non
              </button>
              <button
                type="button"
                onClick={cancelSchedule}
                disabled={busy}
                className="text-xs uppercase font-bold tracking-wider px-4 h-9 rounded-md bg-destructive text-white hover:opacity-90 disabled:opacity-50"
                data-testid="schedule-open-cancel-confirm-yes"
              >
                {busy ? "…" : "Oui, annuler"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spec utilisateur fork 25.32 — popup d'INFORMATION non bloquant
         (un seul bouton « J'ai compris ») quand la programmation tombe
         après la fin de l'abonnement. La programmation est déjà
         enregistrée — il s'agit uniquement de prévenir le merchant. */}
      {postSubInfo && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
          data-testid="schedule-open-post-sub-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setPostSubInfo(null); }}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-post-sub-title"
          >
            <div className="px-5 py-4 border-b border-border bg-[#F18F18]/10">
              <h3
                id="schedule-post-sub-title"
                className="font-display font-black uppercase tracking-[0.04em] text-base text-[#F18F18] flex items-center gap-2"
              >
                <AlertTriangle size={18} /> Attention
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-ink leading-snug">
              <p>
                La programmation a été enregistrée pour une date <strong>postérieure
                à la fin de votre abonnement</strong> ({postSubInfo}).
              </p>
              <p className="mt-2 text-subtle">
                Pour que cette programmation soit prise en compte, pensez à
                renouveler votre abonnement avant cette date.
              </p>
            </div>
            <div className="px-5 py-3 bg-surface/50 border-t border-border flex justify-end">
              <button
                type="button"
                onClick={() => setPostSubInfo(null)}
                className="text-xs uppercase font-bold tracking-wider px-4 h-9 rounded-md bg-brand text-white hover:bg-brand-hover"
                data-testid="schedule-open-post-sub-ack"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
